/*
Description: Comprise Health EMR integration
Date: 03/30/2026
Author: Comprehend PT

Layout
──────
Main tabs (flex headers):  Subjective | Objective | Assessment | Plan | Addendums

getQuestions() iterates every S/O/A/P tab and returns one Flex question
per tab. Each question's Structure contains every textarea in that tab:

  {
    Key: "Subjective",
    Question: "Subjective",
    Type: "Flex",
    Structure: {
      "Subjective":                      { "Existing Value": "", Input: fn },
      "Chief Complaint / Today's Update":{ "Existing Value": "", Input: fn },
      "Functional Limitations":          { "Existing Value": "", Input: fn },
      "HEP Compliance Notes":            { "Existing Value": "", Input: fn },
      "Patient Goals":                   { "Existing Value": "", Input: fn },
      "Medication Changes":              { "Existing Value": "", Input: fn },
      "Imaging Results":                 { "Existing Value": "", Input: fn },
      "New Diagnosis":                   { "Existing Value": "", Input: fn },
    }
  }

Short-/Long-Term goals are surfaced as their own "Goal List" questions (Type
"Flex", Structure = array of goal objects), like WebPTInject. Comprise has two
accordions, so it emits "Plan - Short-Term Goal List" and "Plan - Long-Term
Goal List" (they're subsections of Plan). At fill time we add enough cards,
re-extract, then fill each goal by index.

The Objective tab's clinical tables (ROM, MMT, Special Tests, …) are published
as subsections of the "Objective" question — "Range of Motion", "Manual Muscle
Testing", … — and listed on it in `ParallelSections`. The backend answers each
listed subsection concurrently within one Objective request and returns their
answers nested under their names. The cascading ones publish a pruned flex++ clinical option tree;
dependent values are validated against the live dropdown after their parent is
selected.
*/

import {
  ROM_JOINT_MOVEMENTS,
  MMT_REGION_MOVEMENT_MUSCLES,
  SPECIAL_TESTS_BY_REGION
} from './CompriseClinicalCatalog.ts';

export function getPageInfo() {
  const baseURL = window.location.origin;
  let noteType = 'Default Note';

  // Note Type comes from the header dropdown; fall back to the old header text.
  const noteTypeFromDropdown = getNoteTypeFromDropdown();
  if (noteTypeFromDropdown) {
    return { URL: baseURL, 'Note Type': noteTypeFromDropdown };
  }

  const h4 = document.querySelector<HTMLElement>('h4.modal-title');
  if (h4) {
    // Prefer appointment type from modal header (e.g. "| Appointment: Initial Evaluation")
    const modalHeader = h4.closest('.modal-header') || h4.parentElement;
    if (modalHeader) {
      for (const span of Array.from(modalHeader.querySelectorAll('span'))) {
        const match = (span.textContent || '').match(/Appointment:\s*(.+)/i);
        if (match) {
          noteType = match[1].trim();
          break;
        }
      }
    }
    // Fallback to h4 title text
    if (noteType === 'Default Note') {
      const clone = h4.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('ng-icon, span, svg').forEach((el) => el.remove());
      const raw = clone.textContent?.trim() || '';
      if (raw) noteType = raw.replace(/^(add new|edit)\s+/i, '').trim();
    }
  } else {
    const heading = document.querySelector<HTMLElement>('h1,h2,h3,h4,h5,h6');
    if (heading) {
      const raw = (heading.textContent || '')
        .replace(/\s*-\s*.*$/, '')
        .replace(/^(add new|edit)\s+/i, '')
        .trim();
      if (raw) noteType = raw;
    }
  }

  return { URL: baseURL, 'Note Type': noteType };
}

function getNoteTypeFromDropdown(): string | null {
  const select = document.querySelector<HTMLSelectElement>(
    'select#noteSoapNoteType, select[name="noteSoapNoteType"]'
  );
  if (!select) return null;
  const opt = select.selectedOptions[0];
  if (!opt || opt.disabled) return null;
  const text = (opt.textContent || '').trim();
  if (!text || /select note type/i.test(text)) return null;
  return text;
}

export function getPatientInfo() {
  let best: HTMLElement | null = null;
  for (const el of document.querySelectorAll<HTMLElement>(
    'h1,h2,h3,h4,h5,h6,p,div,span'
  )) {
    const text = (el.textContent || '').trim();
    if (!/\S\s*\(MRN:\s*[^)\s]+\)/i.test(text)) continue;
    if (!best || text.length < (best.textContent || '').trim().length) best = el;
  }
  if (best) {
    return parsePatientText((best.textContent || '').trim());
  }
  console.warn('Comprise Health: Unable to locate patient info on this page.');
  return null;
}

function parsePatientText(text: string) {
  text = text
    .replace(/^(add new|edit)\s+soap\s+note\s*/i, '')
    .replace(/^-\s*/, '')
    .trim();

  const parenIdx = text.indexOf('(');
  const namePart = (parenIdx === -1 ? text : text.slice(0, parenIdx)).trim();
  const metaPart =
    parenIdx === -1
      ? ''
      : text
          .slice(parenIdx + 1)
          .replace(/\)\s*$/, '')
          .trim();

  const parts = namePart.split(/\s+/).filter(Boolean);
  const firstName =
    parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0] || '';
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

  if (!firstName) {
    console.warn('Comprise Health: Unable to extract patient name.');
    return null;
  }

  const mrnMatch = metaPart.match(/MRN:\s*([^\s,)]+)/i);
  const emrID = mrnMatch
    ? mrnMatch[1].trim()
    : `${firstName}${lastName}`.replace(/\s+/g, '');

  const dobInMeta = metaPart.match(/DOB:\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
  const dob = dobInMeta ? dobInMeta[1] : findDOBOnPage() || '';

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

function findDOBOnPage(): string | null {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent || '';
    if (/DOB:/i.test(text)) {
      const m = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
      if (m) return m[1];
    }
  }
  return null;
}

var questions: any[] = [];
var questionInteractions: Record<string, any> = {};

// getQuestions cache to avoid re-scanning back-to-back (the "double sync").
var cachedQuestions: any[] | null = null;
var lastGetQuestionsAt = 0;
var answeringInProgress = 0;
const GET_QUESTIONS_TTL_MS = 15000;
let clinicalSectionsEnabled = true;

export async function getQuestions(
  _isWebPTUser?: any,
  _dropdownCache?: any,
  _replyType?: any,
  _empowerNewVersion?: any,
  targetTab?: string | null,
  enableClinicalSections?: boolean
) {
  return await questionMutex.runExclusive(async () => {
    // Select the mode inside the scan lock so concurrent requests cannot clear
    // an in-progress scan or reuse questions from the other mode.
    // Background readers omit the flag and must preserve the selected mode.
    const next = enableClinicalSections === undefined
      ? clinicalSectionsEnabled
      : enableClinicalSections === true;
    if (next !== clinicalSectionsEnabled) {
      clinicalSectionsEnabled = next;
      cachedQuestions = null;
      questions = [];
      questionInteractions = {};
    }

    const soapNav = getSOAPTabNav();
    if (!soapNav) {
      return await getQuestionsForCurrentTab();
    }

    // Single-tab mode: navigate only to the requested tab and scan just that one
    if (targetTab) {
      await navigateToTab(targetTab);
      return await getQuestionsForCurrentTab();
    }

    // Return cache if a recent full scan is still valid or we're mid-fill.
    const now = Date.now();
    if (
      cachedQuestions &&
      (now - lastGetQuestionsAt < GET_QUESTIONS_TTL_MS || answeringInProgress > 0)
    ) {
      return cachedQuestions;
    }

    const tabLinks = Array.from(
      soapNav.querySelectorAll<HTMLAnchorElement>('a[ngbnavlink]')
    );

    questions = [];
    questionInteractions = {};

    const originalActiveTab = getActiveSOAPTabLabel();

    beginOverlay("Scanning all SOAP sections… This won't take long.", {
      title: 'ComprehendPT'
    });
    try {
      for (const tabLink of tabLinks) {
        tabLink.click();
        await sleep(500);

        const tabLabel = getTabLabel(tabLink);
        await clickExpandAll();

        const structure: Record<string, any> = {};
        document.querySelectorAll<HTMLTextAreaElement>('textarea[name]').forEach((textarea) => {
          const fieldLabel = getLabelForTextarea(textarea);
          if (!fieldLabel) return;

          const textareaName = textarea.getAttribute('name')!;
          const accordionHeading = getAccordionSectionLabel(textarea);

          structure[fieldLabel] = {
            'Existing Value': {
              'Existing Value': textarea.value || '',
              Input: async (val: string, replyType: string) => {
                if (isEmptyOrNull(val)) return;
                await navigateToTab(tabLabel);

                let fresh = document.querySelector<HTMLTextAreaElement>(
                  `textarea[name="${textareaName}"]`
                );
                if (!fresh || fresh.getBoundingClientRect().height <= 0) {
                  fresh = findTextareaByFieldLabel(fieldLabel, accordionHeading);
                }
                if (!fresh) {
                  await clickExpandAll();
                  fresh = findTextareaByFieldLabel(fieldLabel, accordionHeading);
                }
                if (fresh) {
                  fillTextarea(fresh, val, replyType);
                  // Top-level textareas (no accordion) are susceptible to Angular
                  // auto-save reverting the fill after tab navigation settles.
                  // Re-verify after a short delay and re-apply if the value was lost.
                  if (!accordionHeading) {
                    await sleep(2500);
                    const verify = document.querySelector<HTMLTextAreaElement>(
                      `textarea[name="${textareaName}"]`
                    );
                    if (verify && !verify.value.trim()) {
                      fillTextarea(verify, val, replyType);
                    }
                  }
                }
              }
            }
          };
        });

        if (Object.keys(structure).length > 0) {
          questions.push({ Key: tabLabel, Question: tabLabel, Type: 'Flex', Structure: structure });
          questionInteractions[tabLabel] = { Type: 'Flex', Structure: structure };
        }

        pushGoalListQuestions(tabLabel);
        await refreshCascadeOptions();
        pushClinicalTableQuestions(tabLabel);
      }

      // Restore to the tab the user was on before scanning, not always the first tab
      if (tabLinks.length > 0) await navigateToTab(originalActiveTab);

      // Copy so a later single-tab scan can't mutate the cache by reference.
      cachedQuestions = questions.slice();
      lastGetQuestionsAt = Date.now();

      return questions;
    } finally {
      endOverlay();
    }
  });
}

async function getQuestionsForCurrentTab() {
  beginOverlay("Scanning the current section… This won't take long.", {
    title: 'ComprehendPT'
  });
  try {
    const tabLabel = getActiveSOAPTabLabel();
    await clickExpandAll();

    const structure: Record<string, any> = {};
    document.querySelectorAll<HTMLTextAreaElement>('textarea[name]').forEach((textarea) => {
      const fieldLabel = getLabelForTextarea(textarea);
      if (!fieldLabel) return;
      structure[fieldLabel] = {
        'Existing Value': {
          'Existing Value': textarea.value || '',
          Input: (val: string, replyType: string) => {
            if (isEmptyOrNull(val)) return;
            fillTextarea(textarea, val, replyType);
          }
        }
      };
    });

    // This path does not reset `questions` the way a full scan does, so a
    // repeat scan of the same tab must replace that tab's question rather than
    // add a second one — the clinical tables hang off this object, and a stale
    // copy would both win the lookup below and keep them from being re-attached.
    if (clinicalSectionsEnabled) {
      questions = questions.filter((q) => q.Key !== tabLabel);
    }
    if (Object.keys(structure).length > 0) {
      questions.push({ Key: tabLabel, Question: tabLabel, Type: 'Flex', Structure: structure });
      questionInteractions[tabLabel] = { Type: 'Flex', Structure: structure };
    }

    pushGoalListQuestions(tabLabel);
    await refreshCascadeOptions();
    pushClinicalTableQuestions(tabLabel);
    return questions;
  } finally {
    endOverlay();
  }
}

// Goals: Short-/Long-Term goal cards in the Plan tab.
const GOAL_SECTIONS = [
  { kind: 'stg', label: 'Short-Term Goal', tag: 'app-short-term-goals-accordion', heading: 'short-term goals' },
  { kind: 'ltg', label: 'Long-Term Goal', tag: 'app-long-term-goals-accordion', heading: 'long-term goals' }
];
const GOAL_SUBFIELDS = [
  'Goal Description',
  'Measurable Criteria',
  'Expected Timeframe',
  'Current Status'
];

const GOAL_STATUS_OPTIONS = ['In Progress', 'Met', 'Discontinued'];
const GOAL_TIMEFRAME_OPTIONS: Record<string, string[]> = {
  stg: ['2 weeks', '3 weeks', '4 weeks', '6 weeks'],
  ltg: ['6 weeks', '8 weeks', '12 weeks', '6 months']
};

function getGoalSection(kind: string): HTMLElement | null {
  const cfg = GOAL_SECTIONS.find((s) => s.kind === kind);
  if (!cfg) return null;
  const byTag = document.querySelector<HTMLElement>(cfg.tag);
  if (byTag) return byTag;
  for (const item of document.querySelectorAll<HTMLElement>('[ngbaccordionitem]')) {
    const strong = item.querySelector('[ngbaccordionbutton] strong');
    if (strong && (strong.textContent || '').toLowerCase().includes(cfg.heading)) {
      return item;
    }
  }
  return null;
}

function getGoalCards(section: HTMLElement): HTMLElement[] {
  return Array.from(section.querySelectorAll<HTMLElement>('.card')).filter(
    (c) => !!c.querySelector('textarea')
  );
}

function getGoalAddButton(section: HTMLElement): HTMLButtonElement | null {
  for (const btn of section.querySelectorAll<HTMLButtonElement>('button')) {
    if (/\badd\b[\s\S]*goal/i.test(btn.textContent || '')) return btn;
  }
  return null;
}

async function ensureGoalCards(kind: string, count: number): Promise<HTMLElement[]> {
  let section = getGoalSection(kind);
  if (!section) return [];
  let cards = getGoalCards(section);
  let guard = 0;
  while (cards.length < count && guard < count + 3) {
    const addBtn = getGoalAddButton(section);
    if (!addBtn) break;
    addBtn.click();
    await sleep(350);
    section = getGoalSection(kind) || section;
    cards = getGoalCards(section);
    guard++;
  }
  return cards;
}

function getGoalFieldEl(
  card: HTMLElement,
  kind: string,
  subField: string
): HTMLElement | null {
  const prefix =
    subField === 'Goal Description'
      ? `${kind}-text-`
      : subField === 'Measurable Criteria'
      ? `${kind}-criteria-`
      : subField === 'Expected Timeframe'
      ? `${kind}-timeframe-`
      : `${kind}-status-`;
  const byId = card.querySelector<HTMLElement>(`[id^="${prefix}"]`);
  if (byId) return byId;

  if (subField === 'Goal Description') return card.querySelector('textarea');
  if (subField === 'Measurable Criteria')
    return card.querySelector('input[type="text"], input:not([type])');
  const selects = card.querySelectorAll<HTMLSelectElement>('select');
  return subField === 'Expected Timeframe' ? selects[0] || null : selects[1] || null;
}

function readGoalFieldValue(el: HTMLElement | null): string {
  if (el instanceof HTMLSelectElement) {
    const opt = el.selectedOptions[0];
    return opt && opt.value !== '' ? (opt.textContent || '').trim() : '';
  }
  if (el && 'value' in el) return (el as HTMLInputElement).value || '';
  return '';
}

function getGoalFieldChoices(
  kind: string,
  subField: string,
  el: HTMLElement | null
): string[] | null {
  const fallback =
    subField === 'Current Status'
      ? GOAL_STATUS_OPTIONS
      : subField === 'Expected Timeframe'
      ? GOAL_TIMEFRAME_OPTIONS[kind] || []
      : null;
  if (!fallback) return null;

  if (el instanceof HTMLSelectElement) {
    const live = Array.from(el.options)
      .filter((o) => o.value !== '')
      .map((o) => (o.textContent || '').trim())
      .filter(Boolean);
    if (live.length) return live;
  }
  return fallback;
}

function setNativeFieldValue(
  el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  value: string
) {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : el instanceof HTMLSelectElement
      ? HTMLSelectElement.prototype
      : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

  const lView = (el as any).__ngContext__;
  if (Array.isArray(lView)) {
    for (const item of lView) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const ctrl = item.control;
        if (ctrl && typeof ctrl.setValue === 'function') {
          ctrl.setValue(value, { emitEvent: false });
          if (typeof ctrl.markAsDirty === 'function') ctrl.markAsDirty();
          break;
        }
      }
    }
  }

  if (nativeSetter) nativeSetter.call(el, value);
  else (el as any).value = value;

  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  el.dispatchEvent(new Event('blur', { bubbles: true }));
}

function setSelectValue(select: HTMLSelectElement, aiValue: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  const target = norm(aiValue);
  const options = Array.from(select.options);
  const opt =
    options.find(
      (o) => norm(o.value) === target || norm(o.textContent || '') === target
    ) ||
    options.find((o) => {
      const t = norm(o.textContent || '');
      return !!t && (t.includes(target) || target.includes(t));
    });
  if (!opt) return false;
  setNativeFieldValue(select, opt.value);
  return true;
}

async function fillGoalField(
  kind: string,
  slotIndex: number,
  subField: string,
  tabLabel: string,
  val: string,
  replyType: string
) {
  if (isEmptyOrNull(val)) return;

  let section = getGoalSection(kind);
  if (!section) {
    await navigateToTab(tabLabel);
    await clickExpandAll();
    section = getGoalSection(kind);
  }
  if (!section) return;

  const cards = await ensureGoalCards(kind, slotIndex + 1);
  const card = cards[slotIndex];
  if (!card) return;

  const el = getGoalFieldEl(card, kind, subField);
  if (!el) return;

  if (el instanceof HTMLSelectElement) {
    setSelectValue(el, val);
  } else {
    fillTextarea(el as HTMLInputElement | HTMLTextAreaElement, val, replyType);
  }
}

function goalListKey(kind: string): string {
  return kind === 'ltg'
    ? 'Plan - Long-Term Goal List'
    : 'Plan - Short-Term Goal List';
}

// Sub-field interaction object for one goal card (card=null → empty template slot).
function buildGoalSlot(
  kind: string,
  slotIndex: number,
  tabLabel: string,
  card: HTMLElement | null
): Record<string, any> {
  const sub: Record<string, any> = {};
  for (const subField of GOAL_SUBFIELDS) {
    const el = card ? getGoalFieldEl(card, kind, subField) : null;
    const existingValue = readGoalFieldValue(el);
    const choices = getGoalFieldChoices(kind, subField, el);
    sub[subField] = {
      'Existing Value': existingValue,
      ...(choices ? { Choices: choices } : {}),
      Input: async (val: string, replyType: string) =>
        fillGoalField(kind, slotIndex, subField, tabLabel, val, replyType)
    };
  }
  return sub;
}

// (Re)builds the "<kind> Goal List" interaction: Structure = array of goal
// objects, one per visible card. Re-callable so new cards get live Input fns.
function extractGoalList(kind: string, tabLabel: string) {
  const section = getGoalSection(kind);
  if (!section || section.getClientRects().length === 0) return;

  const cards = getGoalCards(section);
  // At least one slot so the AI sees the sub-field shape even with no cards yet.
  const count = Math.max(cards.length, 1);

  const structure: any[] = [];
  for (let i = 0; i < count; i++) {
    structure.push(buildGoalSlot(kind, i, tabLabel, cards[i] || null));
  }

  const listKey = goalListKey(kind);
  questionInteractions[listKey] = {
    Key: listKey,
    Question: listKey,
    Type: 'Flex',
    Structure: structure,
    Elements: [],
    GoalKind: kind,
    Tab: tabLabel
  };
}

// Surfaces the Goal List questions for goal accordions present on `tabLabel`.
function pushGoalListQuestions(tabLabel: string) {
  for (const cfg of GOAL_SECTIONS) {
    const section = getGoalSection(cfg.kind);
    if (!section || section.getClientRects().length === 0) continue;

    extractGoalList(cfg.kind, tabLabel);
    const listKey = goalListKey(cfg.kind);
    const qi = questionInteractions[listKey];
    if (qi && !questions.some((q) => q.Key === listKey)) {
      questions.push({
        Key: listKey,
        Question: listKey,
        Type: 'Flex',
        Structure: qi.Structure
      });
    }
  }
}


/* ─── Clinical tables: ROM / MMT / Special Tests ──────────────────────────── */
/*
Three Objective-tab sections with a variable number of rows, surfaced as their
own "Flex" questions with an array Structure, exactly like the goal accordions
above.

What makes them different from goals: some dropdowns only offer options once
their parent dropdown is set — ROM movement depends on joint, MMT movement
depends on region and muscle depends on movement, special-test name depends on
body region. The EMR renders only the *current* parent's children, so a scan
can never enumerate them. The AI names dependent values in standard clinical
terms; the fill sets each parent first, waits for the child <select> to
repopulate, then matches deterministically (normalised, exact). A value we
cannot match is left blank and recorded in the returned error list, with
nothing shown to the therapist.

Two traps worth knowing:
  * ROM and MMT each embed a "Previous ..." accordion holding the last note's
    table for comparison. clickExpandAll() expands it, so it is live and
    visible by the time we fill — every lookup here filters it out by heading.
  * The Side dropdown has no blank option, so a row we add always reads "Left"
    until the AI says otherwise. There is no way to express "not stated".
*/

type ClinicalFieldKind = 'select' | 'number' | 'text' | 'checkbox' | 'result';

interface ClinicalField {
  label: string; // Structure key the AI answers with
  kind: ClinicalFieldKind;
  /**
   * This dropdown says *which* measurement the row is, not what was measured.
   * Rows are matched on these, and one the therapist already set is never
   * rewritten — so a row can only be reused when every identity they filled in
   * agrees with the AI's.
   */
  identity?: boolean;
  /**
   * The control has no blank state (the Side select has no placeholder), so its
   * current value is not evidence the therapist chose it. Always writable, or a
   * reused row would silently keep "Left" when the transcript said Right.
   */
  noBlankState?: boolean;
  header?: string; // table layout: the <th> text this column starts with
  idPrefix?: string; // card layout: stable element id prefix
  emrLabel?: string; // card layout: the on-screen <label> text
  nameAttr?: string; // single layout: the control's name attribute
  dependsOn?: string; // parent field label, for cascading selects
  enabledBy?: { label: string; value: string }; // control is disabled until this holds
  description?: string; // hint sent to the AI in place of Choices
  // Used only when an empty section has no live control to read choices from.
  fallbackChoices?: string[];
  normalise?: (value: string) => string;
}

interface ClinicalSectionConfig {
  kind: string;
  key: string;
  tag: string;
  heading: string;
  layout: 'table' | 'cards' | 'single';
  /** Absent on a 'single' section: it holds one fixed set of fields. */
  addButton?: RegExp;
  /** 'cards': the id prefix cards are counted by. Defaults to the first field's. */
  cardAnchor?: string;
  fields: ClinicalField[]; // declared in fill order: parents before children
  tree?: ClinicalTreeConfig; // catalog metadata used for live option refresh/validation
}

// "4/5" / "4 out of 5" → "4", so a grade phrased as a fraction still matches the
// option values (5, 4+, 4, 4-, ...).
function normaliseGrade(value: string): string {
  return value.replace(/\s*(?:\/|\bout\s+of\b)\s*5\s*$/i, '').trim();
}

const CLINICAL_SECTIONS: ClinicalSectionConfig[] = [
  {
    kind: 'rom',
    key: 'Objective - Range of Motion',
    tag: 'app-range-of-motion-accordion',
    heading: 'range of motion measurements',
    layout: 'table',
    addButton: /\badd\b[\s\S]*\brom\b/i,
    tree: {
      branches: ['Joint', 'Movement'],
      cascade: ROM_JOINT_MOVEMENTS,
      perOptionLeaf: { field: 'Degrees', optionsFrom: 'Type' },
      repeatBy: 'Side',
      repeatCombined: 'Bilateral',
      requires: ['WNL', 'Notes']
    },
    fields: [
      {
        label: 'Joint',
        kind: 'select',
        header: 'joint',
        identity: true,
        fallbackChoices: [
          'Shoulder', 'Elbow', 'Forearm', 'Wrist', 'Hand', 'Thumb',
          'Fingers (index/middle/ring/little)', 'Hip', 'Knee', 'Ankle', 'Foot',
          'Toes', 'Cervical Spine', 'Thoracic Spine', 'Lumbar Spine',
          'TMJ (Temporomandibular joint)'
        ]
      },
      {
        label: 'Movement',
        kind: 'select',
        header: 'movement',
        dependsOn: 'Joint',
        identity: true
      },
      {
        label: 'Type',
        kind: 'select',
        header: 'type',
        identity: true,
        fallbackChoices: ['Active', 'Passive', 'Active-Assisted']
      },
      { label: 'Degrees', kind: 'number', header: 'degrees' },
      {
        label: 'Side',
        kind: 'select',
        header: 'side',
        noBlankState: true,
        fallbackChoices: ['Left', 'Right', 'Bilateral']
      },
      {
        label: 'WNL',
        kind: 'checkbox',
        header: 'wnl',
        description:
          'Answer "Yes" only if the transcript says this is within normal limits.'
      },
      { label: 'Notes', kind: 'text', header: 'notes' }
    ]
  },
  {
    kind: 'mmt',
    key: 'Objective - Manual Muscle Testing',
    tag: 'app-manual-muscle-testing-accordion',
    heading: 'manual muscle testing',
    layout: 'table',
    addButton: /\badd\b[\s\S]*\bmmt\b/i,
    tree: {
      branches: ['Region', 'Movement'],
      cascade: MMT_REGION_MOVEMENT_MUSCLES,
      dependentLeaf: 'Muscle',
      repeatBy: 'Side',
      repeatCombined: 'Bilateral',
      // Muscle and Notes can be guessed on an otherwise irrelevant branch.
      // A strength row is clinically meaningful only when it has a grade.
      requires: ['Grade']
    },
    fields: [
      {
        label: 'Region',
        kind: 'select',
        header: 'region',
        identity: true,
        fallbackChoices: [
          'Cervical Spine', 'Scapula', 'Shoulder', 'Elbow', 'Forearm', 'Wrist',
          'Hand / Fingers', 'Thumb', 'Thoracic / Lumbar / Trunk', 'Hip', 'Knee',
          'Ankle', 'Foot / Toes'
        ]
      },
      {
        label: 'Movement',
        kind: 'select',
        header: 'movement',
        dependsOn: 'Region',
        identity: true
      },
      {
        label: 'Muscle',
        kind: 'select',
        header: 'muscle',
        dependsOn: 'Movement',
        identity: true
      },
      {
        label: 'Grade',
        kind: 'select',
        header: 'grade',
        fallbackChoices: [
          '5 - Normal strength',
          '4+ - Near normal, minimal weakness',
          '4 - Moderate resistance, full motion',
          '4- - Some weakness, less than full resistance',
          '3+ - Full ROM against gravity + minimal resistance',
          '3 - Full ROM against gravity',
          '3- - Less than full ROM against gravity',
          '2+ - Full ROM in gravity-eliminated + slight resistance',
          '2 - Full ROM in gravity-eliminated',
          '2- - Partial ROM in gravity-eliminated',
          '1 - Muscle activation/palpable contraction only',
          '0 - No contraction'
        ],
        normalise: normaliseGrade
      },
      {
        label: 'Side',
        kind: 'select',
        header: 'side',
        noBlankState: true,
        fallbackChoices: ['Left', 'Right', 'Bilateral']
      },
      { label: 'Notes', kind: 'text', header: 'notes' }
    ]
  },
  {
    kind: 'special',
    key: 'Objective - Special Tests',
    tag: 'app-special-tests-accordion',
    heading: 'special tests',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*special\s*test/i,
    tree: {
      branches: ['Body Region', 'Test Name'],
      cascade: SPECIAL_TESTS_BY_REGION,
      requires: ['Result', 'Description']
    },
    fields: [
      {
        label: 'Body Region',
        kind: 'select',
        idPrefix: 'bodyRegion',
        emrLabel: 'Body Region',
        identity: true,
        fallbackChoices: [
          'Shoulder', 'Elbow', 'Wrist/Hand', 'Cervical Spine', 'Lumbar Spine',
          'Hip', 'Knee', 'Ankle/Foot'
        ]
      },
      {
        label: 'Test Name',
        kind: 'select',
        idPrefix: 'testName',
        emrLabel: 'Test Name',
        dependsOn: 'Body Region',
        identity: true
      },
      { label: 'Result', kind: 'result', idPrefix: 'result' },
      {
        label: 'Severity',
        kind: 'select',
        idPrefix: 'severity',
        emrLabel: 'Severity',
        enabledBy: { label: 'Result', value: 'Positive' },
        description: 'Only applicable when Result is Positive.',
        fallbackChoices: ['Mild', 'Moderate', 'Severe']
      },
      {
        label: 'Description',
        kind: 'text',
        idPrefix: 'description',
        emrLabel: 'Description / Findings'
      }
    ]
  },
  /*
  The sections below have no cascading dropdown: every list is fixed, so the
  options read off the rendered card are the whole set and no catalog or tree
  is needed. They are cards like Special Tests, addressed by the id suffix
  their fields share — except Gait & Posture, which renders one fixed set of
  fields with no ids at all.

  Two of them, Interventions and Modalities, put the *same* ids on the page at
  once ("duration0", "intensity0"). Every lookup here is scoped to the section
  element, which is what keeps them apart; nothing may reach for a bare
  document.getElementById.
  */
  {
    kind: 'gait',
    key: 'Objective - Gait & Posture',
    tag: 'app-gait-posture-accordion',
    heading: 'gait & posture assessment',
    layout: 'single',
    fields: [
      // Labels here carry no `for` and the controls no id, so each field is
      // found by its label text, falling back to the control's name.
      //
      // These carry fallbackChoices where the card sections do not: this is the
      // only section with no "Add" button, so if its body is not rendered when
      // we scan there is no row to read the options off and no way to make one.
      {
        label: 'Gait Pattern',
        kind: 'select',
        emrLabel: 'Gait Pattern',
        nameAttr: 'gaitPattern',
        fallbackChoices: [
          'Normal', 'Antalgic (limping)', 'Trendelenburg', 'Ataxic', 'Steppage',
          'Waddling', 'Hemiplegic', 'Parkinsonian', 'Other'
        ]
      },
      {
        label: 'Assistive Device',
        kind: 'select',
        emrLabel: 'Assistive Device',
        nameAttr: 'assistiveDevice',
        fallbackChoices: [
          'Single Point Cane', 'Quad Cane', 'Walker (Standard)', 'Rolling Walker',
          'Crutches', 'Wheelchair', 'Other'
        ]
      },
      {
        label: 'Posture (Standing)',
        kind: 'select',
        emrLabel: 'Posture (Standing)',
        nameAttr: 'postureStanding',
        fallbackChoices: [
          'Normal alignment', 'Forward head posture', 'Rounded shoulders',
          'Increased thoracic kyphosis', 'Increased lumbar lordosis', 'Scoliosis',
          'Other'
        ]
      },
      {
        label: 'Posture (Sitting)',
        kind: 'select',
        emrLabel: 'Posture (Sitting)',
        nameAttr: 'postureSitting',
        fallbackChoices: [
          'Normal alignment', 'Forward head posture', 'Slouched', 'Asymmetrical',
          'Other'
        ]
      },
      {
        label: 'Balance',
        kind: 'select',
        emrLabel: 'Balance',
        nameAttr: 'balance',
        fallbackChoices: [
          'Normal', 'Mildly impaired', 'Moderately impaired', 'Severely impaired',
          'Requires contact guard'
        ]
      },
      {
        label: 'Gait Speed',
        kind: 'select',
        emrLabel: 'Gait Speed',
        nameAttr: 'gaitSpeed',
        fallbackChoices: ['Normal', 'Fast', 'Slow', 'Variable']
      }
      // "Additional Observations" is deliberately absent: it is a
      // textarea[name], so the per-tab scan already surfaces it. Listing it
      // here too would have the AI fill the same box from two questions.
    ]
  },
  {
    kind: 'functional',
    key: 'Objective - Functional Tests',
    tag: 'app-functional-tests-accordion',
    heading: 'functional tests',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*functional\s*test/i,
    fields: [
      {
        label: 'Test Name',
        kind: 'select',
        idPrefix: 'testName',
        emrLabel: 'Test Name',
        identity: true,
        description:
          'Name the side when the test has one — "Single-Leg Balance (Right)".'
      },
      { label: 'Overall Result', kind: 'text', idPrefix: 'result', emrLabel: 'Overall Result' },
      {
        label: 'Difficulty / Observations',
        kind: 'text',
        idPrefix: 'difficultyNotes',
        emrLabel: 'Difficulty / Observations'
      }
    ]
  },
  {
    kind: 'intervention',
    key: 'Objective - Interventions Performed',
    tag: 'app-interventions-accordion',
    heading: 'interventions performed',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*intervention/i,
    fields: [
      // Type, Duration and Intensity are captioned by <span>s rather than
      // <label>s, so the id prefix is the only handle they have.
      { label: 'Type', kind: 'select', idPrefix: 'interventionType', identity: true },
      { label: 'Duration', kind: 'number', idPrefix: 'duration', description: 'Minutes.' },
      { label: 'Intensity', kind: 'select', idPrefix: 'intensity' },
      { label: 'Description', kind: 'text', idPrefix: 'description', emrLabel: 'Description' },
      {
        label: 'Patient Response',
        kind: 'text',
        idPrefix: 'patientResponse',
        emrLabel: 'Patient Response'
      },
      {
        label: 'Modifications / Adjustments',
        kind: 'text',
        idPrefix: 'modifications',
        emrLabel: 'Modifications / Adjustments'
      }
    ]
  },
  {
    kind: 'modality',
    key: 'Objective - Modalities',
    tag: 'app-modalities-accordion',
    heading: 'modalities',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*modality/i,
    fields: [
      {
        label: 'Modality Type',
        kind: 'select',
        idPrefix: 'modalityType',
        emrLabel: 'Modality Type',
        identity: true
      },
      {
        label: 'Body Part / Area',
        kind: 'text',
        idPrefix: 'bodyPart',
        emrLabel: 'Body Part / Area'
      },
      {
        label: 'Duration',
        kind: 'number',
        idPrefix: 'duration',
        emrLabel: 'Duration',
        description: 'Minutes.'
      },
      {
        label: 'Intensity / Setting',
        kind: 'select',
        idPrefix: 'intensity',
        emrLabel: 'Intensity / Setting'
      },
      {
        label: 'Notes / Patient Response',
        kind: 'text',
        idPrefix: 'modalityNotes',
        emrLabel: 'Notes / Patient Response'
      }
    ]
  },
  {
    kind: 'outcome',
    key: 'Objective - Outcome Measures / PROMs',
    tag: 'app-outcome-measures-accordion',
    heading: 'outcome measures',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*outcome\s*measure/i,
    fields: [
      // Measure Name first: the EMR sets Score's max from it, so a score
      // written before it is chosen can be clamped or rejected.
      {
        label: 'Measure Name',
        kind: 'select',
        idPrefix: 'measureName',
        emrLabel: 'Measure Name',
        identity: true
      },
      {
        label: 'Score',
        kind: 'number',
        idPrefix: 'score',
        emrLabel: 'Score',
        description: 'The raw score, as a number.'
      },
      {
        label: 'Interpretation',
        kind: 'select',
        idPrefix: 'interpretation',
        emrLabel: 'Interpretation'
      },
      {
        label: 'Clinical Notes',
        kind: 'text',
        idPrefix: 'measureNotes',
        emrLabel: 'Clinical Notes'
      }
      // Date Administered is left to the therapist; Score Range and
      // Percentage Score are displays the EMR computes.
    ]
  },
  {
    kind: 'vital',
    key: 'Objective - Vitals',
    tag: 'app-vitals-accordion',
    heading: 'vitals',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*vital/i,
    fields: [
      {
        label: 'Vital Type',
        kind: 'select',
        idPrefix: 'vitalType',
        emrLabel: 'Vital Type',
        identity: true
      },
      {
        label: 'Value',
        kind: 'text',
        idPrefix: 'vitalValue',
        emrLabel: 'Value',
        description:
          'The reading only, without its unit — "128/82", "72", "98.6".'
      },
      {
        label: 'Clinical Notes',
        kind: 'text',
        idPrefix: 'vitalNotes',
        emrLabel: 'Clinical Notes'
      }
      // Unit is readonly: the EMR fills it from Vital Type. Date/Time is left
      // to the therapist.
    ]
  },
  {
    kind: 'observation',
    key: 'Objective - Observations / Palpation',
    tag: 'app-observations-palpation-accordion',
    heading: 'observations / palpation',
    layout: 'cards',
    addButton: /\badd\b[\s\S]*observation/i,
    fields: [
      {
        label: 'Category',
        kind: 'select',
        idPrefix: 'observationCategory',
        emrLabel: 'Category',
        identity: true
      },
      {
        label: 'Body Area',
        kind: 'select',
        idPrefix: 'observationBodyArea',
        emrLabel: 'Body Area',
        identity: true,
        // Every limb option is side-suffixed ("Knee (Right)"), and a bare
        // "knee" matches two, which matchOption refuses rather than guesses.
        description: 'Name the side for a limb — "Knee (Right)", "Shoulder (Left)".'
      },
      {
        label: 'Findings',
        kind: 'text',
        idPrefix: 'observationFindings',
        emrLabel: 'Findings'
      },
      {
        label: 'Severity',
        kind: 'select',
        idPrefix: 'observationSeverity',
        emrLabel: 'Severity'
      },
      {
        label: 'Additional Notes',
        kind: 'text',
        idPrefix: 'observationNotes',
        emrLabel: 'Additional Notes'
      }
    ]
  }
];

/* --- locating the section, and staying out of the "Previous ..." accordion --- */

function getClinicalSection(cfg: ClinicalSectionConfig): HTMLElement | null {
  const byTag = document.querySelector<HTMLElement>(cfg.tag);
  if (byTag) return byTag;
  for (const item of document.querySelectorAll<HTMLElement>('[ngbaccordionitem]')) {
    const strong = item.querySelector('[ngbaccordionbutton] strong');
    if (!strong) continue;
    const text = normaliseKey(strong.textContent || '');
    // "Previous Range of Motion Measurements" also contains the heading.
    if (text.startsWith('previous')) continue;
    if (text.includes(cfg.heading)) return item;
  }
  return null;
}

/** True when `el` sits inside a "Previous ..." accordion within `section`. */
function isInPreviousSubsection(el: Element, section: HTMLElement): boolean {
  let item = el.closest('[ngbaccordionitem]');
  while (item && section.contains(item)) {
    const strong = item.querySelector('[ngbaccordionbutton] strong');
    if (strong && /^previous\b/i.test((strong.textContent || '').trim())) return true;
    item = item.parentElement?.closest('[ngbaccordionitem]') || null;
  }
  return false;
}

function getClinicalAddButton(
  cfg: ClinicalSectionConfig,
  section: HTMLElement
): HTMLButtonElement | null {
  if (!cfg.addButton) return null;
  for (const btn of section.querySelectorAll<HTMLButtonElement>('button')) {
    if (isInPreviousSubsection(btn, section)) continue;
    if (cfg.addButton.test(btn.textContent || '')) return btn;
  }
  return null;
}

/* --- table layout: <th> text drives the column index --- */

function getClinicalTable(section: HTMLElement): HTMLTableElement | null {
  for (const table of section.querySelectorAll<HTMLTableElement>('table')) {
    if (!isInPreviousSubsection(table, section)) return table;
  }
  return null;
}

function getClinicalTableRows(
  section: HTMLElement,
  table: HTMLTableElement
): HTMLTableRowElement[] {
  return Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr')).filter(
    (row) => !isInPreviousSubsection(row, section) && !!row.querySelector('select, input')
  );
}

function getColumnIndex(table: HTMLTableElement, header: string): number {
  let cells = Array.from(table.querySelectorAll<HTMLElement>('thead th'));
  if (cells.length === 0) cells = Array.from(table.querySelectorAll<HTMLElement>('th'));
  for (let i = 0; i < cells.length; i++) {
    // startsWith so "Grade (0–5)" still matches "grade".
    if (normaliseKey(cells[i].textContent || '').startsWith(header)) return i;
  }
  return -1;
}

function findControlInCell(cell: HTMLElement, field: ClinicalField): HTMLElement | null {
  switch (field.kind) {
    case 'select':
      return cell.querySelector('select');
    case 'checkbox':
      return cell.querySelector('input[type="checkbox"]');
    case 'number':
      return (
        cell.querySelector('input[aria-label="Degrees"]') ||
        cell.querySelector('input[type="number"]')
      );
    case 'text':
      return cell.querySelector('textarea') || cell.querySelector('input[type="text"]');
    default:
      return null;
  }
}

/* --- card layout: the row ordinal maps to the id suffix the card's fields share --- */

/**
 * The card ordinals present in a section, read off the id suffix of its anchor
 * field — the one field every card of that section always renders.
 *
 * The suffix must be the whole remainder of the id, not a prefix match: two
 * sections on the Objective tab share field ids ("duration0" belongs to both
 * Interventions and Modalities), and within one section a prefix match would
 * also pull in a longer id that happens to start the same way.
 */
function getCardIndices(cfg: ClinicalSectionConfig, section: HTMLElement): number[] {
  const anchor = cardAnchorPrefix(cfg);
  if (!anchor) return [];
  const pattern = new RegExp(`^${anchor}(\\d+)$`);
  const found = new Set<number>();
  for (const el of section.querySelectorAll<HTMLElement>(`[id^="${anchor}"]`)) {
    if (isInPreviousSubsection(el, section)) continue;
    const match = (el.id || '').match(pattern);
    if (match) found.add(Number(match[1]));
  }
  return Array.from(found).sort((a, b) => a - b);
}

/** The id prefix cards are counted by: declared, else the first field carrying one. */
function cardAnchorPrefix(cfg: ClinicalSectionConfig): string | null {
  if (cfg.cardAnchor) return cfg.cardAnchor;
  const field = cfg.fields.find((f) => f.idPrefix);
  return field?.idPrefix || null;
}

/**
 * document.getElementById would hand back a duplicate id from a nested
 * "Previous …" accordion (which `section.contains` then happily accepts,
 * because it sits inside the section) — and writing there edits the previous
 * note. Resolve ids within the section, skipping that subtree.
 */
function getElementByIdInSection(section: HTMLElement, id: string): HTMLElement | null {
  if (!/^[A-Za-z][\w-]*$/.test(id)) return null;
  for (const el of section.querySelectorAll<HTMLElement>(`[id="${id}"]`)) {
    if (!isInPreviousSubsection(el, section)) return el;
  }
  return null;
}

/** The element a `for` attribute points at, when that `for` belongs to card `domIndex`. */
function findByCardLabel(
  section: HTMLElement,
  domIndex: number,
  labelText: string
): HTMLElement | null {
  for (const label of section.querySelectorAll<HTMLLabelElement>('label[for]')) {
    if (isInPreviousSubsection(label, section)) continue;
    const forId = label.getAttribute('for') || '';
    const match = forId.match(/(\d+)$/);
    if (!match || Number(match[1]) !== domIndex) continue;
    if (normaliseKey(label.textContent || '') !== normaliseKey(labelText)) continue;
    const el = getElementByIdInSection(section, forId);
    if (el) return el;
  }
  return null;
}

/**
 * Label → control for a section that renders one fixed set of fields, where the
 * labels carry no `for` and the controls no id (Gait & Posture). Matches the
 * label's text, then takes the first control it introduces — its own subtree
 * order, never the page's.
 */
function findByAdjacentLabel(
  section: HTMLElement,
  labelText: string
): HTMLElement | null {
  const CONTROLS = 'select, textarea, input:not([type="hidden"])';
  const want = normaliseKey(labelText);

  for (const label of section.querySelectorAll<HTMLLabelElement>('label')) {
    if (isInPreviousSubsection(label, section)) continue;
    if (normaliseKey(label.textContent || '') !== want) continue;

    const forId = label.getAttribute('for');
    if (forId) {
      const byId = getElementByIdInSection(section, forId);
      if (byId) return byId;
    }
    for (let sib = label.nextElementSibling; sib; sib = sib.nextElementSibling) {
      if (sib.matches(CONTROLS)) return sib as HTMLElement;
      const nested = sib.querySelector<HTMLElement>(CONTROLS);
      if (nested) return nested;
    }
  }
  return null;
}

/** The control for one field of a 'single' section: by label, else by name. */
function findSingleControl(
  section: HTMLElement,
  field: ClinicalField
): HTMLElement | null {
  if (field.emrLabel) {
    const byLabel = findByAdjacentLabel(section, field.emrLabel);
    if (byLabel) return byLabel;
  }
  if (field.nameAttr) {
    for (const el of section.querySelectorAll<HTMLElement>(
      `[name="${field.nameAttr}"]`
    )) {
      if (!isInPreviousSubsection(el, section)) return el;
    }
  }
  return null;
}

function findCardControl(
  section: HTMLElement,
  domIndex: number,
  field: ClinicalField
): HTMLElement | null {
  if (field.emrLabel) {
    const byLabel = findByCardLabel(section, domIndex, field.emrLabel);
    if (byLabel) return byLabel;
  }
  if (field.idPrefix) {
    return getElementByIdInSection(section, `${field.idPrefix}${domIndex}`);
  }
  return null;
}

/** The Positive / Negative radio for one special-test card. */
function findResultInput(
  section: HTMLElement,
  domIndex: number,
  want: string
): HTMLInputElement | null {
  const byLabel = findByCardLabel(section, domIndex, want);
  if (byLabel instanceof HTMLInputElement) return byLabel;
  const byId = getElementByIdInSection(section, `result${want}${domIndex}`);
  return byId instanceof HTMLInputElement ? byId : null;
}

/* --- reading a row --- */

function getClinicalRowCount(cfg: ClinicalSectionConfig, section: HTMLElement): number {
  // The section itself is the single row; it is always there once the section is.
  if (cfg.layout === 'single') return 1;
  if (cfg.layout === 'table') {
    const table = getClinicalTable(section);
    return table ? getClinicalTableRows(section, table).length : 0;
  }
  return getCardIndices(cfg, section).length;
}

function getClinicalFieldEl(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  field: ClinicalField
): HTMLElement | null {
  if (cfg.layout === 'single') {
    return rowIndex === 0 ? findSingleControl(section, field) : null;
  }
  if (cfg.layout === 'table') {
    const table = getClinicalTable(section);
    if (!table || !field.header) return null;
    const row = getClinicalTableRows(section, table)[rowIndex];
    const column = getColumnIndex(table, field.header);
    if (!row || column < 0) return null;
    const cell = row.children[column] as HTMLElement | undefined;
    return cell ? findControlInCell(cell, field) : null;
  }
  const domIndex = getCardIndices(cfg, section)[rowIndex];
  if (domIndex === undefined) return null;
  return findCardControl(section, domIndex, field);
}

function readClinicalValue(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  field: ClinicalField
): string {
  if (field.kind === 'result') {
    const domIndex = getCardIndices(cfg, section)[rowIndex];
    if (domIndex === undefined) return '';
    for (const want of ['Positive', 'Negative']) {
      const input = findResultInput(section, domIndex, want);
      if (input && input.checked) return want;
    }
    return '';
  }
  const el = getClinicalFieldEl(cfg, section, rowIndex, field);
  if (el instanceof HTMLInputElement && el.type === 'checkbox') {
    return el.checked ? 'Yes' : '';
  }
  return readGoalFieldValue(el);
}

function getClinicalChoices(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  field: ClinicalField
): string[] | null {
  if (field.kind === 'result') return ['Positive', 'Negative'];
  if (field.kind === 'checkbox') return ['Yes', 'No'];
  if (field.kind !== 'select') return null;
  // A dependent list only ever holds the current parent's children, so
  // publishing it would hand the AI a narrow, wrong set.
  if (field.dependsOn) return null;

  const el = getClinicalFieldEl(cfg, section, rowIndex, field);
  if (el instanceof HTMLSelectElement) {
    const live = Array.from(el.options)
      .filter((o) => o.value !== '')
      .map((o) => (o.textContent || '').trim())
      .filter(Boolean);
    if (live.length) return live;
  }
  return field.fallbackChoices || null;
}

/* --- surfacing the questions --- */

function extractClinicalList(cfg: ClinicalSectionConfig, tabLabel: string) {
  const section = getClinicalSection(cfg);
  if (!section || section.getClientRects().length === 0) return;

  // At least one slot so the AI sees the column shape even with no rows yet.
  const count = Math.max(getClinicalRowCount(cfg, section), 1);
  const structure: any[] = [];
  for (let i = 0; i < count; i++) {
    const slot: Record<string, any> = {};
    for (const field of cfg.fields) {
      const choices = getClinicalChoices(cfg, section, i, field);
      slot[field.label] = {
        'Existing Value': readClinicalValue(cfg, section, i, field),
        ...(choices ? { Choices: choices } : {}),
        ...(field.description ? { Description: field.description } : {})
      };
    }
    structure.push(slot);
  }

  questionInteractions[cfg.key] = {
    Key: cfg.key,
    Question: cfg.key,
    Type: 'Flex',
    Structure: structure,
    Elements: [],
    RowKind: cfg.kind,
    Tab: tabLabel
  };
}

/* --- Clinical option tree ------------------------------------------------- */

interface ClinicalTreeConfig {
  /** The two cascading identity fields, outermost first. */
  branches: [string, string];
  /**
   * Option map mirroring `branches`. A `string[]` is just the second level's
   * options; a nested map additionally supplies `dependentLeaf`'s choices for
   * that path.
   */
  cascade: Record<string, string[] | Record<string, string[]>>;
  /** Field whose `_choices` come from the cascade's third level (MMT: Muscle). */
  dependentLeaf?: string;
  /**
   * ROM only. Emits one `"<option> <field>"` leaf per option of `optionsFrom`
   * instead of a single `field` leaf beside an `optionsFrom` dropdown, because
   * AROM and PROM of one movement are two rows here and a single Type leaf
   * could only ever produce one of them.
   */
  perOptionLeaf?: { field: string; optionsFrom: string };
  /**
   * A field whose options each get their own branch under the cascade, so the
   * same measurement can be recorded once per option. Side is the case that
   * matters: a shoulder measured left and right is two rows in this table, and
   * one shared Side leaf could only ever carry one of them.
   */
  repeatBy?: string;
  /**
   * The `repeatBy` option meaning "the same on both sides". Two rows that came
   * back identical apart from their side collapse into one of these, which is
   * how "elbow flexion 120 degrees bilaterally" ends up as the single row a
   * therapist would have typed rather than a Left and a Right.
   */
  repeatCombined?: string;
  /** A row is only worth adding when at least one of these came back filled. */
  requires: string[];
}

/** The second level's options for one first-level choice. */
function cascadeChildren(
  cfg: ClinicalSectionConfig,
  parent: string
): string[] {
  const node = cascadeFor(cfg)[parent];
  if (!node) return [];
  return Array.isArray(node) ? node : Object.keys(node);
}

/** `dependentLeaf`'s choices for one full path, when the cascade carries them. */
function cascadeLeafChoices(
  cfg: ClinicalSectionConfig,
  parent: string,
  child: string
): string[] | null {
  const node = cascadeFor(cfg)[parent];
  if (!node || Array.isArray(node)) return null;
  return node[child] || null;
}

/** Field labels that the tree expresses as something other than a plain leaf. */
function treeStructuralFields(cfg: ClinicalSectionConfig): string[] {
  const tree = cfg.tree;
  if (!tree) return [];
  return [
    ...tree.branches,
    ...(tree.repeatBy ? [tree.repeatBy] : []),
    ...(tree.perOptionLeaf ? [tree.perOptionLeaf.optionsFrom] : [])
  ];
}

/** The leaves the AI answers for one path through the cascade. */
function buildTreeLeaves(
  cfg: ClinicalSectionConfig,
  parent: string,
  child: string
): Record<string, any> {
  const tree = cfg.tree as ClinicalTreeConfig;
  const structural = treeStructuralFields(cfg);
  const leaves: Record<string, any> = {};

  for (const field of cfg.fields) {
    if (structural.includes(field.label)) continue;

    if (tree.perOptionLeaf && field.label === tree.perOptionLeaf.field) {
      const source = cfg.fields.find(
        (f) => f.label === tree.perOptionLeaf!.optionsFrom
      );
      for (const option of source?.fallbackChoices || []) {
        leaves[`${option} ${field.label}`] = {
          _description:
            `${option} ${field.label.toLowerCase()} for this ${tree.branches[1].toLowerCase()}, ` +
            `digits only (e.g. "120"). Leave blank unless the transcript gives this reading.`
        };
      }
      continue;
    }

    const choices =
      field.label === tree.dependentLeaf
        ? cascadeLeafChoices(cfg, parent, child)
        : field.kind === 'checkbox'
          ? ['Yes', 'No']
          : field.kind === 'result'
            ? ['Positive', 'Negative']
            : field.fallbackChoices;

    // The backend protects branches containing arrays from pruning, including
    // _choices. Keep ROM/MMT choices in text and validate them before adding rows.
    if ((cfg.kind === 'rom' || cfg.kind === 'mmt') && choices?.length) {
      leaves[field.label] = {
        _description: [
          field.description,
          `Choose exactly one of: ${choices.map((choice) => JSON.stringify(choice)).join(', ')}. ` +
            'Return an empty string unless this finding was documented in the transcript.'
        ].filter(Boolean).join(' ')
      };
      continue;
    }

    leaves[field.label] = {
      ...(choices && choices.length > 0 ? { _choices: choices } : {}),
      ...(field.description ? { _description: field.description } : {})
    };
  }
  return leaves;
}

/** The options `repeatBy` expands into, or [] for a section without one. */
function treeRepeatOptions(cfg: ClinicalSectionConfig): string[] {
  const label = cfg.tree?.repeatBy;
  if (!label) return [];
  return cfg.fields.find((f) => f.label === label)?.fallbackChoices || [];
}

/**
 * The side a child's own name already fixes, if any.
 *
 * The spine movements are named "Rotation (Left)" / "Sidebending (Right)" and
 * the TMJ's "Lateral Deviation Left" — the direction *is* the measurement, so
 * branching them by side would leave the model choosing between three
 * equivalent slots. It picked Bilateral for both thoracic rotations, which
 * reads as though each was measured on both sides.
 */
function intrinsicRepeatOption(
  cfg: ClinicalSectionConfig,
  child: string
): string | null {
  const match = child.match(/[\s(](Left|Right)\)?\s*$/i);
  if (!match) return null;
  return (
    treeRepeatOptions(cfg).find(
      (option) => option.toLowerCase() === match[1].toLowerCase()
    ) || null
  );
}

/**
 * The node under one (parent, child) path: the leaves directly, or one branch
 * per `repeatBy` option.
 *
 * Each of those branches is `_no_prune`. Letting the pruner choose sides would
 * cost a round per surviving movement, and a pruner that drops the side the
 * transcript *did* mention is exactly the failure this shape exists to stop.
 */
function buildTreeNode(
  cfg: ClinicalSectionConfig,
  parent: string,
  child: string
): Record<string, any> {
  const options = treeRepeatOptions(cfg);
  if (options.length === 0 || intrinsicRepeatOption(cfg, child)) {
    return buildTreeLeaves(cfg, parent, child);
  }

  const node: Record<string, any> = {};
  for (const option of options) {
    // Built per option, not shared: seeding writes into these.
    node[option] = { ...buildTreeLeaves(cfg, parent, child), _no_prune: true };
  }
  return node;
}

/**
 * Copies rows the therapist already filled onto their path in the tree, for two
 * reasons: the AI can see the finding is recorded rather than emitting it a
 * second time, and the backend pruner never drops a branch carrying an existing
 * value, so a branch they started is guaranteed to survive the prune.
 *
 * Skips any field with no blank state — the Side select reads "Left" on an
 * untouched row, which is not evidence anybody chose it.
 */
function seedTreeExistingValues(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  tree: Record<string, any>
) {
  const treeCfg = cfg.tree as ClinicalTreeConfig;
  const structural = treeStructuralFields(cfg);
  const rowCount = getClinicalRowCount(cfg, section);

  for (let i = 0; i < rowCount; i++) {
    const read = (label: string): string => {
      const field = cfg.fields.find((f) => f.label === label);
      return field ? readClinicalValue(cfg, section, i, field) : '';
    };

    let node = tree[read(treeCfg.branches[0])]?.[read(treeCfg.branches[1])];
    // The row's own value picks the branch. Side has no blank state, so an
    // untouched row reads "Left" and seeds there — that is still what the row
    // says, and it keeps the pruner from dropping a branch they have started.
    if (node && treeCfg.repeatBy && !intrinsicRepeatOption(cfg, read(treeCfg.branches[1]))) {
      node = node[read(treeCfg.repeatBy)];
    }
    if (!node) continue;

    if (treeCfg.perOptionLeaf) {
      const option = read(treeCfg.perOptionLeaf.optionsFrom);
      const value = read(treeCfg.perOptionLeaf.field);
      const leaf = `${option} ${treeCfg.perOptionLeaf.field}`;
      if (option && value && node[leaf]) node[leaf]._existingValue = value;
    }
    for (const field of cfg.fields) {
      if (structural.includes(field.label) || field.noBlankState) continue;
      if (field.label === treeCfg.perOptionLeaf?.field) continue;
      const value = read(field.label);
      if (value && node[field.label]) node[field.label]._existingValue = value;
    }
  }
}

/** The flex++ Structure published for a cascading section. */
function buildClinicalTree(
  cfg: ClinicalSectionConfig,
  section: HTMLElement
): Record<string, any> {
  const treeCfg = cfg.tree as ClinicalTreeConfig;

  // Keep the section's existing preference for the page's own list over our
  // static one for the outer dropdown — only the dependent levels have to come
  // from the catalog, because those are what a scan can never see. An option the
  // page offers that the catalog has no children for is skipped rather than sent
  // as an empty branch.
  const parentField = cfg.fields.find((f) => f.label === treeCfg.branches[0]);
  const offered = parentField
    ? getClinicalChoices(cfg, section, 0, parentField)
    : null;
  const parents = (offered || Object.keys(cascadeFor(cfg))).filter(
    (parent) => cascadeChildren(cfg, parent).length > 0
  );

  const tree: Record<string, any> = {};
  for (const parent of parents) {
    const node: Record<string, any> = {};
    for (const child of cascadeChildren(cfg, parent)) {
      node[child] = buildTreeNode(cfg, parent, child);
    }
    tree[parent] = node;
  }
  seedTreeExistingValues(cfg, section, tree);
  return { ...tree, 'flex++': true, prune_early: true };
}

/**
 * flex++ fills every leaf it is given, so a branch the visit never covered still
 * comes back — as the "Not Discussed" sentinel the backend appends to each
 * single-select enum. Those read as blank here.
 */
function treeLeafValue(raw: any): string {
  if (raw === null || raw === undefined) return '';
  const value = String(raw).trim();
  if (isEmptyOrNull(value) || value === 'Not Discussed' || value === '-99999') {
    return '';
  }
  return value;
}

/** The AI's tree, if this answer is one. */
function clinicalAnswerTree(
  cfg: ClinicalSectionConfig,
  answer: any
): Record<string, any> | null {
  if (!cfg.tree) return null;
  const isTree = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
  const candidate = isTree(answer)
    ? isTree(answer.answer)
      ? answer.answer
      : answer
    : null;
  if (!candidate) return null;
  // Restored ROM/MMT pruning can remove the entire section.
  if ((cfg.kind === 'rom' || cfg.kind === 'mmt') && Object.keys(candidate).length === 0) {
    return candidate;
  }
  return Object.keys(candidate).some((k) => k in cascadeFor(cfg))
    ? candidate
    : null;
}

/**
 * Turns the tree back into the row list the fill already understands. With
 * `perOptionLeaf` that is one row per option that came back with a reading;
 * otherwise one row per path, and either way only when `requires` is satisfied —
 * which is what keeps a fully-sentinel branch from adding an empty row.
 */
/**
 * The rows one leaf-set yields: one per `perOptionLeaf` option that came back
 * with a reading, otherwise a single row when `requires` is satisfied. `base`
 * carries whatever the tree already fixed structurally for this path.
 */
function rowsFromLeaves(
  cfg: ClinicalSectionConfig,
  base: Record<string, string>,
  leaves: Record<string, any>
): Record<string, string>[] {
  const treeCfg = cfg.tree as ClinicalTreeConfig;
  const structural = treeStructuralFields(cfg);
  const rows: Record<string, string>[] = [];

  // Everything on this path that the tree did not express structurally.
  const carried: Record<string, string> = {};
  for (const field of cfg.fields) {
    if (structural.includes(field.label)) continue;
    if (field.label === treeCfg.perOptionLeaf?.field) continue;
    carried[field.label] = treeLeafValue(leaves[field.label]);
  }
  const shared = { ...base, ...carried };

  if (treeCfg.perOptionLeaf) {
    const { field, optionsFrom } = treeCfg.perOptionLeaf;
    const source = cfg.fields.find((f) => f.label === optionsFrom);
    for (const option of source?.fallbackChoices || []) {
      const value = treeLeafValue(leaves[`${option} ${field}`]);
      if (!value) continue;
      rows.push({ ...shared, [optionsFrom]: option, [field]: value });
    }
    if (rows.length > 0) return rows;
  }

  if (treeCfg.requires.some((label) => carried[label])) {
    const blanks: Record<string, string> = {};
    for (const label of structural) {
      if (!(label in base)) blanks[label] = '';
    }
    if (treeCfg.perOptionLeaf) blanks[treeCfg.perOptionLeaf.field] = '';
    rows.push({ ...shared, ...blanks });
  }
  return rows;
}

/**
 * Turns the tree back into the row list the fill already understands, so nothing
 * downstream of here changed.
 */
function flattenClinicalTree(
  cfg: ClinicalSectionConfig,
  tree: Record<string, any>
): Record<string, string>[] {
  const treeCfg = cfg.tree as ClinicalTreeConfig;
  const [parentLabel, childLabel] = treeCfg.branches;
  const repeatOptions = treeRepeatOptions(cfg);
  const rows: Record<string, string>[] = [];

  for (const [parent, children] of Object.entries(tree)) {
    if (!(parent in cascadeFor(cfg))) continue;
    if (!children || typeof children !== 'object') continue;
    const offered = cascadeChildren(cfg, parent);

    for (const [child, node] of Object.entries(children as Record<string, any>)) {
      if (!node || typeof node !== 'object') continue;
      if (!offered.includes(child)) continue;
      const path = { [parentLabel]: parent, [childLabel]: child };

      if (repeatOptions.length === 0) {
        rows.push(...rowsFromLeaves(cfg, path, node));
        continue;
      }

      // "Rotation (Left)" was never branched — its side comes from its name.
      const intrinsic = intrinsicRepeatOption(cfg, child);
      if (intrinsic) {
        rows.push(
          ...rowsFromLeaves(cfg, { ...path, [treeCfg.repeatBy!]: intrinsic }, node)
        );
        continue;
      }

      // One branch per repeat option, so the same movement recorded on both
      // sides becomes two rows rather than one that has to pick a side.
      for (const option of repeatOptions) {
        const leaves = (node as Record<string, any>)[option];
        if (!leaves || typeof leaves !== 'object') continue;
        rows.push(
          ...rowsFromLeaves(cfg, { ...path, [treeCfg.repeatBy!]: option }, leaves)
        );
      }
    }
  }
  return collapseRepeatedRows(cfg, rows);
}

/**
 * Folds rows that came back identical apart from their side into a single
 * combined row, which is what the EMR's "Bilateral" option is for. Anything
 * that differs — a degree, a grade, a note — is left as two rows, so the
 * measurements that genuinely differ side to side still get one row each.
 */
function collapseRepeatedRows(
  cfg: ClinicalSectionConfig,
  rows: Record<string, string>[]
): Record<string, string>[] {
  const treeCfg = cfg.tree as ClinicalTreeConfig;
  const { repeatBy, repeatCombined } = treeCfg;
  if (!repeatBy || !repeatCombined) return rows;

  const groups = new Map<string, Record<string, string>[]>();
  const order: string[] = [];
  for (const row of rows) {
    const rest = { ...row };
    delete rest[repeatBy];
    const key = JSON.stringify(
      Object.keys(rest)
        .sort()
        .map((label) => [label, rest[label]])
    );
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(row);
  }

  return order.map((key) => {
    const group = groups.get(key)!;
    // One row per side at most, so more than one means the sides disagreed
    // about nothing — the measurement applies to both.
    return group.length > 1
      ? { ...group[0], [repeatBy]: repeatCombined }
      : group[0];
  });
}

/* --- keeping the option lists fresh -------------------------------------- */
/*
The catalog in CompriseClinicalCatalog.ts is a snapshot, so it goes stale if
Comprise edits a list. These functions read the real lists back out of the EMR
by stepping a parent dropdown through its options and recording what each one
puts in its child, then cache the result so it costs nothing on later notes.

Two rules keep this away from the therapist's data:
  * A section is only scraped when it has **no rows at all**, and the pass works
    in a row it adds itself. The row is unambiguously ours, and the worst a pass
    interrupted mid-way can leave behind is one blank row — never a change to
    something they typed.
  * The parent dropdown is blanked before the row is deleted, so if the delete
    control has moved we are left with an empty row rather than one that reads
    as a measurement.

Cached in the page's own localStorage (the EMR's origin), like MWInject's latch
and Raintree's listbox choices — injectors also run in the embed and iPad
builds, which have no extension storage APIs. Only dropdown labels are stored,
never note content.
*/

const CASCADE_CACHE_KEY = 'comprehendpt_comprise_cascade_v1';
const CASCADE_TTL_MS = 10 * 24 * 60 * 60 * 1000;
/*
Whatever a pass does not reach stays on its previous value and is retried on a
later note, so this only has to be long enough to make progress — not long
enough to finish. Sized to stay well inside the scan the therapist is already
waiting on.
*/
const CASCADE_SCRAPE_BUDGET_MS = 8000;

type CascadeChildren = string[] | Record<string, string[]>;
type CascadeCache = {
  build: string;
  sections: Record<string, Record<string, { children: CascadeChildren; ts: number }>>;
};

/**
 * Which build of the EMR is loaded. Angular serves content-hashed filenames, so
 * these change on a deploy — the only moment the option lists can change. A new
 * build drops the whole cache rather than waiting out the TTL.
 */
function getEmrBuildId(): string {
  return (
    Array.from(document.scripts)
      .map((script) => script.src || '')
      .filter((src) => src.startsWith(window.location.origin))
      .map((src) => src.slice(src.lastIndexOf('/') + 1))
      .filter((name) => /^(main|chunk|polyfills)[-.]/.test(name))
      .sort()
      .join(',') || 'unknown'
  );
}

function loadCascadeCache(): CascadeCache {
  const build = getEmrBuildId();
  try {
    const raw = localStorage.getItem(CASCADE_CACHE_KEY);
    const parsed = raw ? (JSON.parse(raw) as CascadeCache) : null;
    if (parsed && parsed.build === build && parsed.sections) {
      return { build, sections: parsed.sections };
    }
  } catch (_) {
    // Unreadable cache is the same as no cache.
  }
  return { build, sections: {} };
}

function saveCascadeCache(cache: CascadeCache) {
  try {
    localStorage.setItem(CASCADE_CACHE_KEY, JSON.stringify(cache));
  } catch (_) {
    // A cache we cannot persist just means a later note scrapes again.
  }
}

/** Built once per section per page: merging on every lookup would be wasteful. */
const mergedCascades = new Map<string, Record<string, CascadeChildren>>();

/** The catalog, with any still-fresh scraped list layered over it. */
function cascadeFor(cfg: ClinicalSectionConfig): Record<string, CascadeChildren> {
  const cached = mergedCascades.get(cfg.kind);
  if (cached) return cached;

  const merged: Record<string, CascadeChildren> = { ...(cfg.tree?.cascade || {}) };
  const stored = loadCascadeCache().sections[cfg.kind] || {};
  const now = Date.now();
  for (const [parent, entry] of Object.entries(stored)) {
    if (now - entry.ts < CASCADE_TTL_MS) merged[parent] = entry.children;
  }
  mergedCascades.set(cfg.kind, merged);
  return merged;
}

/** A dropdown's real options, without its "Select…" placeholder. */
function readSelectOptions(select: HTMLSelectElement): string[] {
  return Array.from(select.options)
    .filter((option) => option.value !== '')
    .map((option) => (option.textContent || '').trim())
    .filter(Boolean);
}

/**
 * Reads a dependent list after its parent was just set.
 *
 * The child keeps the previous parent's options until Angular re-renders, and
 * having options is not proof they belong to the parent we set — the same trap
 * writeDependentSelect handles for writes, which cost us Knee getting the
 * shoulder's movements. Waiting for the option *nodes* to be replaced is what
 * says the list is this parent's. Two parents that genuinely offer the same
 * list fall through to the timeout with the right answer already on screen, and
 * a control that disappears is the EMR dropping the picker entirely (a movement
 * with a single muscle).
 */
async function readRefreshedOptions(
  cfg: ClinicalSectionConfig,
  field: ClinicalField,
  before: HTMLOptionElement[]
): Promise<string[]> {
  const MAX_WAIT_MS = 1500;
  const ABSENT_GRACE_MS = 250;
  const POLL_MS = 50;

  let el: HTMLElement | null = null;
  for (let waited = 0; waited < MAX_WAIT_MS; waited += POLL_MS) {
    const section = getClinicalSection(cfg);
    el = section ? getClinicalFieldEl(cfg, section, 0, field) : null;

    if (el instanceof HTMLSelectElement) {
      const now = Array.from(el.options);
      const replaced =
        now.length !== before.length || now.some((node, i) => node !== before[i]);
      const options = readSelectOptions(el);
      if (replaced && options.length > 0) return options;
    } else if (waited >= ABSENT_GRACE_MS) {
      return [];
    }
    await sleep(POLL_MS);
  }
  return el instanceof HTMLSelectElement ? readSelectOptions(el) : [];
}

/** The control that removes one row, or null if it is not where we expect. */
function getClinicalRowDeleteButton(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number
): HTMLElement | null {
  if (cfg.layout === 'table') {
    const table = getClinicalTable(section);
    const row = table ? getClinicalTableRows(section, table)[rowIndex] : null;
    const buttons = row ? row.querySelectorAll<HTMLElement>('button') : null;
    return buttons && buttons.length > 0 ? buttons[buttons.length - 1] : null;
  }
  const domIndex = getCardIndices(cfg, section)[rowIndex];
  const anchor = cardAnchorPrefix(cfg);
  if (domIndex === undefined || !anchor) return null;
  const card = getElementByIdInSection(section, `${anchor}${domIndex}`)?.closest(
    '.card'
  ) as HTMLElement | null;
  if (!card) return null;
  return (
    card.querySelector<HTMLElement>('button[title*="Remove"], button[title*="remove"]') ||
    card.querySelector<HTMLElement>('button')
  );
}

/** Reads one parent's children, and for MMT each child's muscles underneath. */
async function scrapeOneParent(
  cfg: ClinicalSectionConfig,
  parent: string,
  deadline: number
): Promise<CascadeChildren | null> {
  const treeCfg = cfg.tree!;
  const section = getClinicalSection(cfg);
  if (!section) return null;

  const parentField = cfg.fields.find((f) => f.label === treeCfg.branches[0]);
  const childField = cfg.fields.find((f) => f.label === treeCfg.branches[1]);
  if (!parentField || !childField) return null;

  const parentEl = getClinicalFieldEl(cfg, section, 0, parentField);
  if (!(parentEl instanceof HTMLSelectElement)) return null;
  const option = matchOption(parentEl, parent);
  if (!option) return null;

  const childBefore = Array.from(
    (getClinicalFieldEl(cfg, section, 0, childField) as HTMLSelectElement | null)
      ?.options || []
  );
  setNativeFieldValue(parentEl, option.value);
  const children = await readRefreshedOptions(cfg, childField, childBefore);
  if (children.length === 0) return null;
  if (!treeCfg.dependentLeaf) return children;

  // MMT's third dropdown depends on the child, so step that too.
  const leafField = cfg.fields.find((f) => f.label === treeCfg.dependentLeaf);
  const bakedForParent = treeCfg.cascade[parent];
  const byChild: Record<string, string[]> = {};

  for (const child of children) {
    if (Date.now() > deadline) break;
    const liveChild = getClinicalFieldEl(cfg, section, 0, childField);
    if (!(liveChild instanceof HTMLSelectElement)) break;
    const childOption = matchOption(liveChild, child);
    if (!childOption) continue;

    const leafBefore = Array.from(
      (leafField
        ? (getClinicalFieldEl(cfg, section, 0, leafField) as HTMLSelectElement | null)
        : null)?.options || []
    );
    setNativeFieldValue(liveChild, childOption.value);
    const read = leafField
      ? await readRefreshedOptions(cfg, leafField, leafBefore)
      : [];
    // A movement with one muscle renders no picker at all, so there is nothing
    // to read — the catalog's single entry is still the right answer.
    byChild[child] =
      read.length > 0
        ? read
        : (bakedForParent && !Array.isArray(bakedForParent)
            ? bakedForParent[child]
            : undefined) || [];
  }

  // A parent we only got half way through would otherwise be cached as
  // complete, hiding the movements we never reached for a full TTL.
  return Object.keys(byChild).length === children.length ? byChild : null;
}

/**
 * Puts the section back the way we found it. Blanking the parent first means a
 * delete we cannot perform still leaves an empty row rather than a populated one.
 */
async function removeScrapeRow(cfg: ClinicalSectionConfig) {
  const section = getClinicalSection(cfg);
  if (!section) return;

  const parentField = cfg.fields.find((f) => f.label === cfg.tree!.branches[0]);
  const parentEl = parentField
    ? getClinicalFieldEl(cfg, section, 0, parentField)
    : null;
  if (parentEl instanceof HTMLSelectElement) setNativeFieldValue(parentEl, '');

  const deleteButton = getClinicalRowDeleteButton(cfg, section, 0);
  if (!deleteButton) return;
  deleteButton.click();
  for (let waited = 0; waited < 1000; waited += 50) {
    await sleep(50);
    const live = getClinicalSection(cfg);
    if (!live || getClinicalRowCount(cfg, live) === 0) return;
  }
}

/** Refreshes whatever this section's cache is missing or has let age out. */
async function scrapeCascadeSection(
  cfg: ClinicalSectionConfig,
  deadline: number
): Promise<boolean> {
  if (!cfg.tree) return false;

  let section = getClinicalSection(cfg);
  if (!section || section.getClientRects().length === 0) return false;
  // Only ever work in an empty section — see the note above.
  if (getClinicalRowCount(cfg, section) !== 0) return false;

  const cache = loadCascadeCache();
  const stored = cache.sections[cfg.kind] || {};
  const isFresh = (parent: string) =>
    stored[parent] && Date.now() - stored[parent].ts < CASCADE_TTL_MS;

  // Decide from the catalog before touching the DOM, so a section that is
  // already fresh never has a row added to it at all.
  if (Object.keys(cfg.tree.cascade).every(isFresh)) return false;

  section = await ensureOneMoreClinicalRow(cfg, section);
  if (getClinicalRowCount(cfg, section) !== 1) return false;

  let scraped = false;
  try {
    const parentField = cfg.fields.find((f) => f.label === cfg.tree!.branches[0]);
    const parentEl = parentField
      ? getClinicalFieldEl(cfg, section, 0, parentField)
      : null;
    if (!(parentEl instanceof HTMLSelectElement)) return false;

    // The page's own list, not the catalog's: it is what the EMR offers today.
    for (const parent of readSelectOptions(parentEl)) {
      if (Date.now() > deadline) break;
      if (isFresh(parent)) continue;
      const children = await scrapeOneParent(cfg, parent, deadline);
      if (!children) continue;
      stored[parent] = { children, ts: Date.now() };
      scraped = true;
    }
  } finally {
    await removeScrapeRow(cfg);
  }

  if (scraped) {
    cache.sections[cfg.kind] = stored;
    saveCascadeCache(cache);
    mergedCascades.delete(cfg.kind);
  }
  return scraped;
}

/**
 * Post-scan pass over the cascading sections on the current tab, mirroring
 * Raintree's listbox-choice pass: it runs inside the scan's overlay so the
 * therapist sees "Scanning…" rather than dropdowns moving on their own, and it
 * gives up on a shared budget so a slow page cannot stretch the scan. Sections
 * not on this tab are skipped by the visibility check.
 */
async function refreshCascadeOptions() {
  if (!clinicalSectionsEnabled) return;
  // Avoid changing row indices while a fill is in progress.
  if (answeringInProgress > 0) return;

  const deadline = Date.now() + CASCADE_SCRAPE_BUDGET_MS;
  for (const cfg of CLINICAL_SECTIONS) {
    if (!cfg.tree || Date.now() > deadline) continue;
    try {
      await scrapeCascadeSection(cfg, deadline);
    } catch (error) {
      // A refresh is best-effort; the catalog still stands in.
      console.error(`${cfg.key} option refresh failed:`, error);
    }
  }
}

/**
 * The name a clinical table is published under inside its tab's question.
 * `cfg.key` stays the internal handle ("Objective - Range of Motion") so
 * questionInteractions and the whole fill path are untouched; the tab prefix it
 * carries — there because these used to be top-level keys — comes off for the
 * name the template and the AI see.
 */
function clinicalSubsectionName(cfg: ClinicalSectionConfig): string {
  const separator = cfg.key.indexOf(' - ');
  return separator === -1 ? cfg.key : cfg.key.slice(separator + 3);
}

/** The tab's own Flex question, created empty if the tab had no textareas. */
function getOrCreateTabQuestion(tabLabel: string) {
  let host = questions.find((q) => q.Key === tabLabel);
  if (!host) {
    const structure: Record<string, any> = {};
    host = { Key: tabLabel, Question: tabLabel, Type: 'Flex', Structure: structure };
    questions.push(host);
    questionInteractions[tabLabel] = { Type: 'Flex', Structure: structure };
  }
  if (!host.ParallelSections) host.ParallelSections = [];
  return host;
}

/**
 * Adds the clinical tables present on `tabLabel` to that tab's question as
 * subsections, rather than publishing each as a question of its own.
 *
 * They were separate questions so the front end would fire one request per
 * table and answer them in parallel — which is also why they showed up as
 * top-level sections everywhere a template key is listed. `ParallelSections`
 * asks the backend to answer these independently within the tab's single request.
 */
function pushClinicalTableQuestions(tabLabel: string) {
  if (!clinicalSectionsEnabled) return;
  for (const cfg of CLINICAL_SECTIONS) {
    const section = getClinicalSection(cfg);
    if (!section || section.getClientRects().length === 0) continue;

    // A table that already went out with an earlier tab in this scan is not
    // published again — the first tab it looked visible on keeps it, as it did
    // when each table was a question of its own.
    const name = clinicalSubsectionName(cfg);
    if (questions.some((q) => q.ParallelSections?.includes(name))) continue;

    extractClinicalList(cfg, tabLabel);
    const qi = questionInteractions[cfg.key];
    if (!qi) continue;

    const host = getOrCreateTabQuestion(tabLabel);
    if (name in host.Structure) {
      // A textarea on the tab already owns the name. Say so rather than
      // shadowing it and filling the wrong thing.
      console.error(`${cfg.key} collides with an existing "${name}" field`);
      continue;
    }
    // Only a cascading section needs the option tree; the rest publish the
    // rows themselves, as the goal accordions do.
    host.Structure[name] = cfg.tree ? buildClinicalTree(cfg, section) : qi.Structure;
    host.ParallelSections.push(name);
  }
}

/* --- filling --- */

/**
 * Normalises for option matching. Folds the unicode dashes to ASCII because
 * this EMR mixes them (the MMT column header is "Grade (0–5)" with an en dash
 * while its option text uses hyphens), and a grade is all dashes.
 */
function normaliseOption(text: string): string {
  return normaliseKey(text.replace(/[\u2010-\u2015\u2212]/g, '-'));
}

/**
 * Drops the trailing noun and any qualifier that this EMR's option labels carry
 * but a dictated name usually does not: every special test is spelled
 * "Hawkins-Kennedy Test", "Tinel's Sign (Ankle)", "Thompson Test (Achilles)",
 * while the therapist just says "Hawkins-Kennedy".
 */
function stripTestSuffix(text: string): string {
  return normaliseOption(text)
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/\s+(test|sign|maneuver|manoeuvre)$/, '')
    .trim();
}

/**
 * Exact match on option value or text, after normalisation. Never a guess: the
 * suffix-tolerant second pass only counts when exactly one option survives it,
 * so an ambiguous name resolves to nothing rather than to the wrong test.
 */
function matchOption(select: HTMLSelectElement, value: string): HTMLOptionElement | null {
  const target = normaliseOption(value);
  if (!target) return null;
  const options = Array.from(select.options).filter((o) => o.value !== '');

  for (const option of options) {
    if (
      normaliseOption(option.value) === target ||
      normaliseOption(option.textContent || '') === target
    ) {
      return option;
    }
  }

  const stripped = stripTestSuffix(value);
  if (!stripped) return null;
  const near = options.filter(
    (o) =>
      stripTestSuffix(o.value) === stripped ||
      stripTestSuffix(o.textContent || '') === stripped
  );
  return near.length === 1 ? near[0] : null;
}

/**
 * Re-reads a dependent <select> until it has real options to match against.
 *
 * Having options is NOT proof they belong to the parent we just set: on a real
 * note the ROM movement column ships 13 options with no joint chosen, and a
 * special-test card can hold another region's test names. That is handled after
 * the write instead — see writeDependentSelect — because a list that Angular
 * has not refreshed yet gets replaced under us, which is detectable, whereas
 * predicting the refresh is not.
 *
 * A control that never appears is a third case: the EMR drops the MMT muscle
 * picker when only one muscle applies, and waiting the full window for
 * something that is not coming costs a second and a half per row.
 */
async function waitForDependentSelect(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  field: ClinicalField
): Promise<HTMLElement | null> {
  const MAX_WAIT_MS = 1500;
  const MAX_WAIT_ABSENT_MS = 400;
  const POLL_MS = 50;
  let el = getClinicalFieldEl(cfg, section, rowIndex, field);
  for (let waited = 0; waited < MAX_WAIT_MS; waited += POLL_MS) {
    if (el instanceof HTMLSelectElement) {
      if (Array.from(el.options).some((o) => o.value !== '')) return el;
    } else if (waited >= MAX_WAIT_ABSENT_MS) {
      return el;
    }
    await sleep(POLL_MS);
    el = getClinicalFieldEl(cfg, section, rowIndex, field);
  }
  return el;
}

/**
 * Writes a dependent dropdown and proves the write survived.
 *
 * If we matched against a list the parent had not refreshed yet, Angular
 * replaces the options a tick later and the selection is dropped — so we
 * re-read, and on a loss re-match against the list that is now there. Only a
 * value that is still selected afterwards counts as written; anything else is
 * reported rather than left looking correct.
 */
async function writeDependentSelect(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  field: ClinicalField,
  select: HTMLSelectElement,
  value: string
): Promise<{ ok: boolean; offered: string[] }> {
  const SETTLE_MS = 250;
  const offeredOf = (el: HTMLSelectElement) =>
    Array.from(el.options)
      .filter((o) => o.value !== '')
      .map((o) => o.value);

  let current = select;
  for (let attempt = 0; attempt < 2; attempt++) {
    const option = matchOption(current, value);
    if (!option) {
      if (attempt === 1) return { ok: false, offered: offeredOf(current) };
      // The requested option may only exist after the parent refreshes its list.
      await sleep(SETTLE_MS);
      const fresh = getClinicalFieldEl(cfg, section, rowIndex, field);
      if (!(fresh instanceof HTMLSelectElement)) return { ok: false, offered: [] };
      current = fresh;
      continue;
    }
    setNativeFieldValue(current, option.value);

    await sleep(SETTLE_MS);
    const fresh = getClinicalFieldEl(cfg, section, rowIndex, field);
    if (!(fresh instanceof HTMLSelectElement)) return { ok: true, offered: [] };
    if (normaliseOption(fresh.value) === normaliseOption(option.value)) {
      return { ok: true, offered: [] };
    }
    // The parent's refresh landed after our write; try once against the new list.
    current = fresh;
  }
  return { ok: false, offered: offeredOf(current) };
}

/** Severity stays disabled until Angular has processed the positive result. */
async function waitUntilEnabled(el: HTMLSelectElement | HTMLInputElement) {
  const MAX_WAIT_MS = 1500;
  const POLL_MS = 50;
  for (let waited = 0; waited < MAX_WAIT_MS && el.disabled; waited += POLL_MS) {
    await sleep(POLL_MS);
  }
}

function setClinicalCheckbox(el: HTMLInputElement, value: string): boolean {
  const norm = normaliseKey(value);
  const on = /^(yes|true|wnl|within normal limits|checked|1)$/.test(norm);
  const off = /^(no|false|0|none|unchecked)$/.test(norm);
  if (!on && !off) return false;
  if (el.checked !== on) {
    el.click();
    if (el.checked !== on) {
      el.checked = on;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  return true;
}

function setResultRadio(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  value: string
): boolean {
  const domIndex = getCardIndices(cfg, section)[rowIndex];
  if (domIndex === undefined) return false;
  const norm = normaliseKey(value);
  const want =
    norm === 'positive' ? 'Positive' : norm === 'negative' ? 'Negative' : null;
  if (!want) return false;

  const input = findResultInput(section, domIndex, want);
  if (!input) return false;
  if (!input.checked) {
    input.click();
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  // These radios have no `name`, so the browser does not treat them as one
  // group and the opposite choice stays checked. Clear it directly — no event,
  // which would read as the user picking that other value.
  const other = findResultInput(
    section,
    domIndex,
    want === 'Positive' ? 'Negative' : 'Positive'
  );
  if (other && other !== input && other.checked) other.checked = false;
  return input.checked;
}

/** The AI's value for one field, or '' — tolerates a differently-cased key. */
function pickClinicalValue(aiRow: Record<string, any>, label: string): string {
  const key = label in aiRow ? label : fuzzyFindKey(aiRow, label);
  const raw = key ? aiRow[key] : undefined;
  if (isEmptyOrNull(typeof raw === 'string' ? raw.trim() : raw)) return '';
  return String(raw).trim();
}

/** ROM/MMT use text choices, so reject unsupported findings before creating rows. */
function validatedMeasurementRow(
  cfg: ClinicalSectionConfig,
  row: Record<string, any>
): Record<string, any> | null {
  if (cfg.kind !== 'rom' && cfg.kind !== 'mmt') return row;

  const result = { ...row };
  for (const field of cfg.fields) {
    result[field.label] = treeLeafValue(pickClinicalValue(row, field.label));
  }

  if (cfg.kind === 'rom') {
    const degrees = result.Degrees;
    const reading = /^-?\d+(?:\.\d+)?(?:\s*(°|degrees?|mm))?$/i.exec(degrees);
    const hasReading = !!reading && (
      reading[1]?.toLowerCase() !== 'mm' || (
        normaliseOption(result.Joint) === 'tmj (temporomandibular joint)' &&
        normaliseOption(result.Movement) === 'depression (mouth opening in mm)'
      )
    );
    const wnl = normaliseKey(result.WNL);
    const isWnl = /^(yes|true|wnl|within normal limits|checked|1)$/.test(wnl);
    if (wnl && !isWnl && !/^(no|false|0|none|unchecked)$/.test(wnl)) return null;
    if (!hasReading && !isWnl) return null;
    result.Degrees = hasReading ? degrees : '';
    return result;
  }

  const grade = normaliseOption(normaliseGrade(result.Grade));
  const grades = cfg.fields.find((field) => field.label === 'Grade')?.fallbackChoices || [];
  const gradeChoice = grades.find(
    (choice) => normaliseOption(choice) === grade || normaliseOption(choice.split(' - ')[0]) === grade
  );
  if (!gradeChoice) return null;
  result.Grade = gradeChoice.split(' - ')[0];

  if (result.Muscle) {
    const region = Object.keys(cascadeFor(cfg)).find(
      (choice) => normaliseOption(choice) === normaliseOption(result.Region)
    );
    const movement = region && cascadeChildren(cfg, region).find(
      (choice) => normaliseOption(choice) === normaliseOption(result.Movement)
    );
    if (!region || !movement) return null;
    const muscles = cascadeLeafChoices(cfg, region, movement) || [];
    let muscle = muscles.find(
      (choice) => normaliseOption(choice) === normaliseOption(result.Muscle)
    );
    // Populated sections aren't scraped. Their matching live dropdown can
    // still offer a valid muscle that the bundled catalog hasn't learned yet.
    if (!muscle) {
      const section = getClinicalSection(cfg);
      const muscleField = cfg.fields.find((field) => field.label === 'Muscle')!;
      const regionField = cfg.fields.find((field) => field.label === 'Region')!;
      const movementField = cfg.fields.find((field) => field.label === 'Movement')!;
      for (let i = 0; section && i < getClinicalRowCount(cfg, section); i++) {
        if (normaliseOption(readClinicalValue(cfg, section, i, regionField)) !== normaliseOption(region) ||
            normaliseOption(readClinicalValue(cfg, section, i, movementField)) !== normaliseOption(movement)) continue;
        const select = getClinicalFieldEl(cfg, section, i, muscleField);
        if (select instanceof HTMLSelectElement) {
          muscle = matchOption(select, result.Muscle)?.value;
          if (muscle) break;
        }
      }
    }
    if (!muscle) return null;
    result.Muscle = muscle;
  }
  return result;
}

/**
 * Fills one row in cfg.fields order — parents before the dropdowns that depend
 * on them, which is why this does not iterate the AI's answer keys.
 *
 * In a row the therapist created we never change a dropdown they already set;
 * their selection is what made the dependent list correct in the first place.
 */
async function fillClinicalRow(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowIndex: number,
  aiRow: Record<string, any>,
  isNewRow: boolean,
  replyType: string,
  errors: any[]
) {
  const label = (field: ClinicalField) => `Row ${rowIndex + 1}.${field.label}`;

  for (const field of cfg.fields) {
    const rawValue = pickClinicalValue(aiRow, field.label);
    if (!rawValue) continue;
    const value = field.normalise ? field.normalise(rawValue) : rawValue;

    if (field.enabledBy) {
      const gate = cfg.fields.find((f) => f.label === field.enabledBy!.label);
      const current = gate ? readClinicalValue(cfg, section, rowIndex, gate) : '';
      // Negative or unstated result → leave severity blank.
      if (normaliseKey(current) !== normaliseKey(field.enabledBy.value)) continue;
    }

    if (field.kind === 'result') {
      if (!setResultRadio(cfg, section, rowIndex, value)) {
        errors.push({ Field: `Row ${rowIndex + 1}.${field.label}`, Error: `No "${value}" option` });
      }
      continue;
    }

    // A dependent list can only be trusted once its parent actually holds a
    // value; otherwise whatever options are sitting there belong to something
    // else. Refuse rather than pick from the wrong list.
    if (field.dependsOn) {
      const parent = cfg.fields.find((f) => f.label === field.dependsOn);
      const parentValue = parent ? readClinicalValue(cfg, section, rowIndex, parent) : '';
      if (!parentValue.trim()) {
        errors.push({
          Field: label(field),
          Error: `${field.dependsOn} is not set, so "${value}" could not be matched`
        });
        continue;
      }
    }

    const el = field.dependsOn
      ? await waitForDependentSelect(cfg, section, rowIndex, field)
      : getClinicalFieldEl(cfg, section, rowIndex, field);

    if (!el) {
      // The EMR omits the MMT muscle picker when only one muscle applies.
      if (!field.dependsOn) {
        errors.push({ Field: label(field), Error: 'Field not found' });
      }
      continue;
    }

    if (el instanceof HTMLSelectElement) {
      // Never rewrite an identity the therapist already chose — it is what made
      // this row match in the first place. Side is exempt: it has no blank
      // state, so its value is not evidence of a choice.
      if (!isNewRow && field.identity && el.value !== '') continue;

      await waitUntilEnabled(el);
      if (el.disabled) {
        errors.push({ Field: label(field), Error: 'Field is still disabled; not written' });
        continue;
      }

      if (field.dependsOn) {
        const written = await writeDependentSelect(cfg, section, rowIndex, field, el, value);
        if (!written.ok) {
          errors.push({
            Field: label(field),
            Error: `"${value}" is not one of: ${written.offered.join(', ') || '(none offered)'}`
          });
        }
        continue;
      }

      const option = matchOption(el, value);
      if (!option) {
        errors.push({
          Field: label(field),
          Error: `"${value}" is not one of: ${Array.from(el.options)
            .filter((o) => o.value !== '')
            .map((o) => o.value)
            .join(', ') || '(none offered)'}`
        });
        continue;
      }
      setNativeFieldValue(el, option.value);
      continue;
    }

    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      if (!setClinicalCheckbox(el, value)) {
        errors.push({ Field: label(field), Error: `Not a yes/no value: "${value}"` });
      }
      continue;
    }

    if (field.kind === 'number') {
      const digits = value.match(/-?\d+(?:\.\d+)?/);
      if (!digits) {
        errors.push({ Field: label(field), Error: `No number in "${value}"` });
        continue;
      }
      setNativeFieldValue(el as HTMLInputElement, digits[0]);
      continue;
    }

    fillTextarea(el as HTMLInputElement | HTMLTextAreaElement, value, replyType);
  }
}

// Normalises a clinical answer to an array of rows: bare array, { answer: [...] }
// envelope, a single row object, or the { "0": {...}, "1": {...} } shape the
// revert path produces when it flattens an array Structure.
function normalizeClinicalAnswer(
  answer: any,
  cfg: ClinicalSectionConfig
): any[] | null {
  // A cascading section's answer arrives as the tree buildClinicalTree published.
  const tree = clinicalAnswerTree(cfg, answer);
  if (tree) return flattenClinicalTree(cfg, tree);
  if (Array.isArray(answer)) return answer;
  if (answer && Array.isArray(answer.answer)) return answer.answer;
  if (answer && typeof answer === 'object') {
    if (cfg.fields.some((f) => f.label in answer)) return [answer];
    const keys = Object.keys(answer);
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
      return keys.sort((a, b) => Number(a) - Number(b)).map((k) => answer[k]);
    }
  }
  return null;
}

/**
 * Picks the row this answer belongs in.
 *
 * A row may only be reused when every identity dropdown the therapist already
 * filled in agrees with this answer. Matching on the first column alone was
 * enough to drop a measurement's numbers onto a different one — an existing
 * "Shoulder / Flexion" row would absorb the degrees for "Shoulder / Extension"
 * and read as though the therapist had measured flexion.
 *
 * Returns the row index, or -1 for "add a row".
 */
function claimClinicalRow(
  cfg: ClinicalSectionConfig,
  section: HTMLElement,
  rowCount: number,
  claimed: Set<number>,
  aiRow: Record<string, any>
): number {
  const identityFields = cfg.fields.filter((f) => f.identity);
  let best = -1;
  let bestScore = 0;
  let firstBlank = -1;

  for (let i = 0; i < rowCount; i++) {
    if (claimed.has(i)) continue;

    // Left is the default on a blank row. Once a finding exists, its side is
    // part of that measurement; a non-default side is also an explicit choice.
    const sideField = cfg.fields.find((field) => field.label === cfg.tree?.repeatBy);
    if (sideField) {
      const side = readClinicalValue(cfg, section, i, sideField);
      const hasFinding = cfg.fields.some((field) =>
        !field.identity && !field.noBlankState &&
        !!readClinicalValue(cfg, section, i, field).trim()
      );
      if (side && (side !== sideField.fallbackChoices?.[0] || hasFinding) &&
          normaliseOption(side) !== normaliseOption(pickClinicalValue(aiRow, sideField.label))) {
        continue;
      }
    }

    let score = 0;
    let compatible = true;
    let anySet = false;

    for (const field of identityFields) {
      const current = readClinicalValue(cfg, section, i, field);
      if (!current.trim()) continue;
      anySet = true;
      const want = pickClinicalValue(aiRow, field.label);
      // Set on the row but absent from (or different in) the answer: we cannot
      // show this is the same measurement, so leave the row alone.
      if (!want || normaliseOption(current) !== normaliseOption(want)) {
        compatible = false;
        break;
      }
      score++;
    }

    if (!compatible) continue;
    if (!anySet) {
      if (firstBlank === -1) firstBlank = i;
      continue;
    }
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }

  if (best !== -1) return best;
  return firstBlank;
}

async function ensureOneMoreClinicalRow(
  cfg: ClinicalSectionConfig,
  section: HTMLElement
): Promise<HTMLElement> {
  const before = getClinicalRowCount(cfg, section);
  const addBtn = getClinicalAddButton(cfg, section);
  if (!addBtn) return section;
  addBtn.click();
  const MAX_WAIT_MS = 1500;
  const POLL_MS = 50;
  let current = section;
  for (let waited = 0; waited < MAX_WAIT_MS; waited += POLL_MS) {
    await sleep(POLL_MS);
    current = getClinicalSection(cfg) || current;
    if (getClinicalRowCount(cfg, current) > before) break;
  }
  return current;
}

async function answerClinicalListQuestion(
  questionObject: any,
  answer: any,
  replyType: string
) {
  return await questionMutex.runExclusive(async () => {
    if (!clinicalSectionsEnabled) {
      return { Question: questionObject.Key, Error: 'Comprise clinical sections are disabled' };
    }
    const cfg = CLINICAL_SECTIONS.find((c) => c.kind === questionObject.RowKind);
    if (!cfg) {
      return { Question: questionObject.Key, Error: 'Unknown clinical section' };
    }

    const parsed = normalizeClinicalAnswer(answer, cfg);
    if (!parsed) {
      console.error(`${cfg.key} answer was not array-shaped:`, answer);
      return { Question: cfg.key, Error: `${cfg.key} answer was not array-shaped` };
    }
    // Validate both tree answers and cached row arrays before touching the DOM.
    const rows = parsed.filter(
      (r) =>
        r &&
        typeof r === 'object' &&
        Object.values(r).some(
          (v) => !isEmptyOrNull(typeof v === 'string' ? v.trim() : v)
        )
    ).map((row) => validatedMeasurementRow(cfg, row))
      .filter((row): row is Record<string, any> => row !== null);

    // Nothing to write: skip the tab navigation and the expand-all, which cost
    // about a second each. The backend answers every question it is asked, so
    // with ten clinical sections on the page most of those answers are empty
    // and this is most of a fill's wall time.
    if (rows.length === 0) return null;

    const tabLabel: string = questionObject.Tab;
    if (tabLabel) {
      await navigateToTab(tabLabel);
      await clickExpandAll();
      await sleep(300);
    }

    let section = getClinicalSection(cfg);
    if (!section) {
      return { Question: cfg.key, Error: `${cfg.key} section not found` };
    }

    const errors: any[] = [];
    const claimed = new Set<number>();

    for (let i = 0; i < rows.length; i++) {
      section = getClinicalSection(cfg) || section;

      const rowCount = getClinicalRowCount(cfg, section);
      let rowIndex = claimClinicalRow(cfg, section, rowCount, claimed, rows[i]);
      let isNewRow = false;

      if (rowIndex < 0 && cfg.layout === 'single') {
        // One fixed set of fields, and it is already spoken for. Say so rather
        // than letting this read as a row that failed to add.
        errors.push({
          Field: `Row ${i + 1}`,
          Error: `${cfg.key} holds a single set of fields; this row was not written`
        });
        continue;
      }

      if (rowIndex < 0) {
        section = await ensureOneMoreClinicalRow(cfg, section);
        const after = getClinicalRowCount(cfg, section);
        if (after <= rowCount) {
          errors.push({ Field: `Row ${i + 1}`, Error: 'Could not add a row' });
          continue;
        }
        rowIndex = after - 1;
        isNewRow = true;
      }

      claimed.add(rowIndex);
      await fillClinicalRow(
        cfg,
        section,
        rowIndex,
        rows[i],
        isNewRow,
        replyType,
        errors
      );
    }

    extractClinicalList(cfg, tabLabel);

    if (errors.length > 0) {
      console.error(`${cfg.key} errors:`, errors);
    }
    const result = errors.length > 0 ? errors : null;
    // The section is a subsection of its tab's question now, so refresh it
    // there. extractClinicalList above already re-read the rows we just wrote,
    // which is what a cascading section rebuilds its tree from too.
    const cached = cachedQuestions?.find((question) => question.Key === tabLabel);
    const name = clinicalSubsectionName(cfg);
    if (cached?.Structure && name in cached.Structure) {
      cached.Structure[name] = cfg.tree
        ? buildClinicalTree(cfg, section)
        : questionInteractions[cfg.key]?.Structure;
    }
    return result;
  });
}

/* ─── End clinical tables ─────────────────────────────────────────────────── */

/**
 * Finds the S/O/A/P editor nav (not the outer page nav) by checking which
 * ul.nav-tabs contains both "Subjective" and "Objective", then returns the
 * text of the currently active tab link within it.
 */
function getActiveSOAPTabLabel(): string {
  const nav = getSOAPTabNav();
  if (nav) {
    const activeLink = nav.querySelector('a.active') as HTMLElement | null;
    if (activeLink) return getTabLabel(activeLink);
  }
  return 'Note';
}

/** Returns the ul.nav-tabs that contains the S/O/A/P section tabs, or null. */
function getSOAPTabNav(): HTMLElement | null {
  for (const nav of document.querySelectorAll<HTMLElement>('ul.nav-tabs')) {
    const text = (nav.textContent || '').toLowerCase();
    if (text.includes('subjective') && text.includes('objective')) return nav;
  }
  return null;
}

async function navigateToTab(targetLabel: string): Promise<void> {
  const nav = getSOAPTabNav();
  if (!nav) {
    return;
  }
  for (const link of nav.querySelectorAll<HTMLAnchorElement>('a[ngbnavlink]')) {
    if (getTabLabel(link) === targetLabel) {
      if (!link.classList.contains('active')) {
        link.click();
        await sleep(1500);
      }
      return;
    }
  }
}

function getTabLabel(tabLink: HTMLElement): string {
  const clone = tabLink.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('div, ng-icon, svg').forEach((el) => el.remove());
  return (clone.textContent || '').trim() || 'Note';
}

async function clickExpandAll() {
  await sleep(700);

  const collapsed = () =>
    Array.from(document.querySelectorAll<HTMLButtonElement>('[ngbaccordionbutton][aria-expanded="false"]'));

  if (collapsed().length === 0) return;

  for (const btn of document.querySelectorAll<HTMLButtonElement>('button')) {
    const label = (btn.innerText || btn.textContent || '').trim();
    if (label.includes('Expand All')) {
      btn.click();
      break;
    }
  }

  await sleep(200);
  for (const btn of collapsed()) {
    btn.click();
  }

  const POLL_MS = 100;
  const MAX_WAIT_MS = 2000;
  let elapsed = 0;
  while (elapsed < MAX_WAIT_MS) {
    await sleep(POLL_MS);
    elapsed += POLL_MS;
    if (collapsed().length === 0) break;
  }
  await sleep(300);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function fillTextarea(
  el: HTMLInputElement | HTMLTextAreaElement,
  val: string,
  replyType: string
) {
  const newValue = replyType === 'append' && el.value ? el.value + '\n' + val : val;
  setNativeFieldValue(el, newValue);
}

/* ─── Blur Overlay (mirrors WebPTSoap2Inject pattern) ─────────────────────── */
const OVERLAY_ID = 'ComprehendBlurOverlay';
const ACTIVE_CLASS = 'comprehend-blur-active';
const CSS_ID = 'ComprehendBlurOverlayStyles';

let overlayEl: HTMLElement | null = null;
let msgEl: HTMLElement | null = null;
let closeBtn: HTMLButtonElement | null = null;
let lastFocused: HTMLElement | null = null;

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
    html.${ACTIVE_CLASS} { overflow: hidden; }
    body.${ACTIVE_CLASS} { overflow: hidden; }
    html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
      filter: blur(var(--comprehend-blur-amount, 8px)) saturate(0.9);
      pointer-events: none;
      user-select: none;
    }
    #${OVERLAY_ID} {
      position: fixed;
      inset: 0;
      --brand-color: rgb(107, 216, 136);
      z-index: 2147483647;
      display: none;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    #${OVERLAY_ID}[data-open="true"] { display: flex; }
    #${OVERLAY_ID}::before {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.25);
      backdrop-filter: blur(var(--comprehend-blur-amount, 8px)) brightness(0.95);
      -webkit-backdrop-filter: blur(var(--comprehend-blur-amount, 8px)) brightness(0.95);
    }
    #${OVERLAY_ID} .blur-msg {
      position: relative;
      max-width: min(720px, 90vw);
      width: max(320px, 40vw);
      background: rgba(255,255,255,0.9);
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.2);
      padding: 20px 24px;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
      color: #111;
      backdrop-filter: blur(2px);
    }
    #${OVERLAY_ID} .blur-msg h2 {
      margin: 0 0 10px 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--brand-color);
    }
    #${OVERLAY_ID} .blur-msg .blur-text {
      line-height: 1.5;
      font-size: 15px;
      white-space: pre-wrap;
    }
    #${OVERLAY_ID} .blur-close {
      position: fixed;
      top: 12px;
      right: 12px;
      appearance: none;
      border: none;
      background: var(--brand-color);
      color: #fff;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      font-size: 20px;
      line-height: 36px;
      text-align: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    }
    #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
    #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
  `;
  document.head.appendChild(style);
}

function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID) as HTMLElement;
    msgEl = overlayEl.querySelector('.blur-text');
    closeBtn = overlayEl.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'Notice';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);
  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  closeBtn.addEventListener('click', hideOverlay);
  document.addEventListener('keydown', (e) => {
    if (overlayEl?.dataset.open === 'true' && e.key === 'Escape') hideOverlay();
  });
}

function showOverlay(message = '', opts: { title?: string; blurAmount?: number } = {}) {
  injectStyles();
  createOverlay();

  const { title = 'ComprehendPT', blurAmount = 8 } = opts;
  overlayEl!.style.setProperty('--comprehend-blur-amount', String(blurAmount));
  overlayEl!.querySelector('h2')!.textContent = title;
  msgEl!.textContent = message;

  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  closeBtn!.style.display = '';
  overlayEl!.dataset.open = 'true';
  overlayEl!.setAttribute('aria-hidden', 'false');

  lastFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  closeBtn!.focus();
}

function updateOverlay(message = '') {
  if (!overlayEl) createOverlay();
  msgEl!.textContent = message;
}

function hideOverlay() {
  if (!overlayEl) return;
  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);
  if (lastFocused && document.contains(lastFocused)) {
    try { lastFocused.focus(); } catch {}
  }
  lastFocused = null;
}

function isOverlayVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

let __overlayRefCount = 0;

function beginOverlay(message: string, opts?: { title?: string }) {
  __overlayRefCount++;
  if (!isOverlayVisible()) {
    showOverlay(message, opts);
  } else {
    updateOverlay(message);
  }
}

function endOverlay() {
  __overlayRefCount = Math.max(0, __overlayRefCount - 1);
  if (__overlayRefCount === 0 && isOverlayVisible()) hideOverlay();
}
/* ─── End Blur Overlay ─────────────────────────────────────────────────────── */

/**
 * Priority:
 *  1. label[for=id] with fw-semibold (specific, ≥ 2 words) → use as-is
 *  2. Nearest [ngbaccordionitem] header <strong> text
 *     + single-word sibling label appended if it adds context
 *  3. <h6> in nearest .card-body (top-level "Subjective" field)
 *  4. Capitalised name attribute (guaranteed non-empty fallback)
 */
function getLabelForTextarea(textarea: HTMLTextAreaElement): string {
  const id = textarea.getAttribute('id') || '';
  const name = textarea.getAttribute('name') || '';

  // 1. label[for=id]
  let fieldLabel = '';
  if (id) {
    const forLabel = document.querySelector<HTMLLabelElement>(
      `label[for="${id}"]`
    );
    if (forLabel) fieldLabel = cleanText(forLabel.textContent || '');
  }
  // Direct preceding sibling label (avoids grabbing labels from other accordions)
  if (!fieldLabel) {
    let sib = textarea.previousElementSibling as HTMLElement | null;
    while (sib) {
      if (sib.tagName === 'LABEL') {
        fieldLabel = cleanText(sib.textContent || '');
        break;
      }
      sib = sib.previousElementSibling as HTMLElement | null;
    }
  }

  // 2. Accordion section header
  const sectionLabel = getAccordionSectionLabel(textarea);
  if (sectionLabel) {
    if (!fieldLabel) return sectionLabel;
    // "Patient Goals" already contains "Goals" → use accordion text alone
    if (sectionLabel.toLowerCase().includes(fieldLabel.toLowerCase()))
      return sectionLabel;
    // Single-word generic label → append to section ("HEP Compliance Notes")
    if (fieldLabel.split(/\s+/).length === 1)
      return `${sectionLabel} ${fieldLabel}`;
    // Multi-word specific label inside a differently-named section →
    // keep the hierarchy: "Medical Updates - Medication Changes"
    return `${sectionLabel} - ${fieldLabel}`;
  }

  if (fieldLabel) return fieldLabel;

  // 3. h6 in card-body (top-level section header, e.g. "Subjective")
  const cardBody = textarea.closest('.card-body');
  if (cardBody) {
    const h6 = cardBody.querySelector('h6');
    if (h6) return (h6.textContent || '').trim();
  }

  // 4. Guaranteed fallback
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

function getAccordionSectionLabel(
  textarea: HTMLTextAreaElement
): string | null {
  const item = textarea.closest('[ngbaccordionitem]');
  if (!item) return null;
  const btn = item.querySelector<HTMLElement>('[ngbaccordionbutton]');
  if (!btn) return null;
  const strong = btn.querySelector('strong');
  if (strong) return (strong.textContent || '').trim();
  const clone = btn.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('svg, ng-icon').forEach((el) => el.remove());
  return (clone.textContent || '').trim() || null;
}

function findTextareaByFieldLabel(
  fieldLabel: string,
  accordionHeading: string | null
): HTMLTextAreaElement | null {
  const norm = (s: string) =>
    s.toLowerCase().replace(/[''''`]/g, "'").replace(/\s+/g, ' ').trim();

  // Strategy A: stable accordion heading text (Angular template text, survives re-renders)
  if (accordionHeading) {
    const normHeading = norm(accordionHeading);
    for (const item of document.querySelectorAll<HTMLElement>('[ngbaccordionitem]')) {
      const btn = item.querySelector<HTMLElement>('[ngbaccordionbutton]');
      if (!btn) continue;
      const strong = btn.querySelector('strong');
      const clone = btn.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('svg, ng-icon').forEach((el) => el.remove());
      const headingText = norm(strong?.textContent || clone.textContent || '');
      if (headingText !== normHeading) continue;

      const tas = Array.from(
        item.querySelectorAll<HTMLTextAreaElement>('textarea[name]')
      ).filter((ta) => ta.getBoundingClientRect().height > 0);

      if (tas.length === 1) return tas[0];
      if (tas.length > 1) {
        const suffix = norm(fieldLabel)
          .slice(normHeading.length)
          .replace(/^[-\s–]+/, '');
        if (suffix) {
          const byLabel = tas.find((ta) => {
            const lbl = norm(getLabelForTextarea(ta));
            return lbl.includes(suffix) || suffix.includes(lbl);
          });
          if (byLabel) return byLabel;
        }
        return tas[0];
      }
    }
  }

  // Strategy B: full getLabelForTextarea scan
  const normTarget = norm(fieldLabel);
  for (const ta of document.querySelectorAll<HTMLTextAreaElement>('textarea[name]')) {
    if (
      ta.getBoundingClientRect().height > 0 &&
      norm(getLabelForTextarea(ta)) === normTarget
    ) {
      return ta;
    }
  }

  // Strategy C: top-level textarea (no accordion) — fallback when accordionHeading is null.
  if (!accordionHeading) {
    const topLevel = Array.from(
      document.querySelectorAll<HTMLTextAreaElement>('textarea[name]')
    ).filter(
      (ta) => ta.getBoundingClientRect().height > 0 && !ta.closest('[ngbaccordionitem]')
    );
    if (topLevel.length > 0) return topLevel[0];
  }

  return null;
}

/** Normalises a string for fuzzy comparison — strips apostrophes/special chars, lowercases. */
function normaliseKey(s: string): string {
  return s
    .replace(/['''\u2019`]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Returns the Structure key that fuzzy-matches the AI-returned key, or null if none found. */
function fuzzyFindKey(
  structure: Record<string, any>,
  key: string
): string | null {
  const norm = normaliseKey(key);
  for (const k of Object.keys(structure)) {
    if (normaliseKey(k) === norm) return k;
  }
  return null;
}

function cleanText(text: string): string {
  return text
    .replace(/\*$/, '')
    .replace(/[\u00A0\u200B]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

class AsyncMutex {
  private chain: Promise<void> = Promise.resolve();
  runExclusive<T>(task: () => Promise<T>): Promise<T> {
    let release!: () => void;
    const willLock = new Promise<void>((resolve) => (release = resolve));
    const previous = this.chain;
    this.chain = previous.then(() => willLock);
    return previous.then(async () => {
      try {
        return await task();
      } finally {
        release();
      }
    });
  }
}
const questionMutex = new AsyncMutex();

function isEmptyOrNull(value: any): boolean {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export async function answerQuestions(answers: any[]) {
  const originalActiveTab = getActiveSOAPTabLabel();
  beginOverlay('Filling in your note… Please wait.', { title: 'ComprehendPT' });
  const errors = [];
  try {
    for (const answer of answers) {
      const error = await answerQuestion(
        answer['Question'],
        answer['Answer'],
        answer['Reply Type']
      );
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
    }

    // Restore to the tab the user was on before filling, not always the first tab
    await navigateToTab(originalActiveTab);

    console.error('Errors:');
    console.error(errors);
  } finally {
    endOverlay();
  }
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
    Description: This function will take the answer and populate the question

    Args:
        question: The question in question
        answer: The answer to the question

    Return: null if success otherwise a string with the error message
    */

  // Mark a fill in progress (and keep the cache fresh) so getQuestions won't
  // re-scan mid-fill.
  answeringInProgress++;
  try {
    return await answerQuestionInner(question, answer, replyType);
  } finally {
    answeringInProgress = Math.max(0, answeringInProgress - 1);
    lastGetQuestionsAt = Date.now();
  }
}

async function answerQuestionInner(question: any, answer: any, replyType: any) {
  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    beginOverlay(`Filling in ${question}…`, { title: 'ComprehendPT' });
    try {
      return await answerFlexQuestion(question, answer, replyType);
    } finally {
      endOverlay();
    }
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

function applyAnswerToQuestionCache(
  question: any,
  answer: any,
  replyType: any,
  flexErrors: any
) {
  try {
    const entry = cachedQuestions?.find((q: any) => q?.Key === question);
    const liveStructure = questionInteractions[question]?.Structure;
    if (!entry || !liveStructure || !answer || typeof answer !== 'object')
      return;

    // answerFlexQuestion reports per-field failures; don't record those as written.
    const failed = new Set(
      (Array.isArray(flexErrors) ? flexErrors : [])
        .map((e: any) => e?.Field)
        .filter(Boolean)
    );

    if (Array.isArray(liveStructure)) {
      const qi = questionInteractions[question];
      if (qi?.GoalKind) {
        extractGoalList(qi.GoalKind, qi.Tab);
      }
      entry.Structure = questionInteractions[question]?.Structure;
      return;
    }

    const structure = entry.Structure;
    if (!structure || typeof structure !== 'object') return;

    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      if (failed.has(fieldKey)) continue;
      if (!fieldDetails || typeof fieldDetails !== 'object') continue;
      const resolvedKey =
        fieldKey in structure ? fieldKey : fuzzyFindKey(structure, fieldKey);
      const field = resolvedKey ? structure[resolvedKey] : null;
      if (!field) continue;

      for (const [subFieldKey, value] of Object.entries(
        fieldDetails as Record<string, any>
      )) {
        if (failed.has(`${fieldKey}.${subFieldKey}`)) continue;
        // Mirror the Input functions: they no-op on empty/None values, so the cache must too.
        if (typeof value !== 'string' || isEmptyOrNull(value)) continue;
        const leaf = field[subFieldKey];
        if (!leaf || typeof leaf !== 'object') continue;
        // Mirror fillTextarea's append semantics.
        const previous = leaf['Existing Value'];
        leaf['Existing Value'] =
          replyType === 'append' && previous ? previous + '\n' + value : value;
      }
    }
  } catch (error) {
    // Never let cache bookkeeping break an answer that already landed in the note.
    console.error('Error updating question cache after answer:', error);
  }
}

async function answerFlexQuestion(question, answer, replyType: any = 'overwrite') {
  const qi = questionInteractions[question];
  if (qi && qi.GoalKind) {
    return await answerGoalListQuestion(qi, answer, replyType);
  }
  if (qi && qi.RowKind) {
    return await answerClinicalListQuestion(qi, answer, replyType);
  }

  // A tab that hosts clinical tables answers for them too, so peel each one off
  // to the section's own filler before the textarea pass below — their values
  // are rows, not { field: { 'Existing Value' } } leaves.
  const clinical = await answerClinicalSubsections(question, answer, replyType);
  answer = clinical.remaining;
  replyType = typeof replyType === 'string' ? replyType : replyType.defaultReplyType;
  // An answer that carried only clinical tables has nothing left for the
  // textarea pass; skip it rather than navigate the tab for an empty answer.
  if (clinical.handled && Object.keys(answer).length === 0) {
    return clinical.errors.length > 0 ? clinical.errors : null;
  }

  const result = await questionMutex.runExclusive(async () => {
    try {
      const errors = [];
      const questionObject = questionInteractions[question];

      if (!questionObject) {
        console.error(`Question with Key "${question}" not found.`);
        return { Question: question, Error: `Question with Key "${question}" not found.` };
      }

      console.log('Question found:', questionObject);

      const structure = questionObject.Structure || {};

      await navigateToTab(question);
      await clickExpandAll();
      await sleep(300);

      for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
        const resolvedKey =
          fieldKey in structure ? fieldKey : fuzzyFindKey(structure, fieldKey);

        const field = resolvedKey ? structure[resolvedKey] : null;

        if (!field) {
          console.error(`Field "${fieldKey}" not found in the Structure.`);
          errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
          continue;
        }

        for (const [subFieldKey, value] of Object.entries(fieldDetails as Record<string, any>)) {
          if (field[subFieldKey]?.Input) {
            try {
              await field[subFieldKey].Input(value, replyType);
            } catch (error) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${error instanceof Error ? error.message : String(error)}`
              });
            }
          } else {
            console.warn(`Input function not found for "${fieldKey}.${subFieldKey}"`);
            errors.push({ Field: `${fieldKey}.${subFieldKey}`, Error: 'Input function not found' });
          }
        }
      }

      if (errors.length > 0) {
        console.error('Errors occurred:', errors);
        return errors;
      }

      return null;
    } catch (error) {
      console.error('Unexpected error:', error);
      return { Question: question, Error: `Unexpected error: ${error instanceof Error ? error.message : String(error)}` };
    }
  });

  applyAnswerToQuestionCache(question, answer, replyType, result);
  if (clinical.errors.length === 0) return result;
  return clinical.errors.concat(
    Array.isArray(result) ? result : result ? [result] : []
  );
}

/**
 * Fills every clinical table this tab's answer carries and returns what is left
 * for the textarea pass. Each section keeps its own question interaction and
 * filler — only where the answer arrives has changed.
 */
async function answerClinicalSubsections(
  tabLabel: string,
  answer: any,
  replyType: any
): Promise<{ remaining: any; errors: any[]; handled: boolean }> {
  const errors: any[] = [];
  let handled = false;
  const hostStructure = questionInteractions[tabLabel]?.Structure;
  if (!clinicalSectionsEnabled || !answer || typeof answer !== 'object' || !hostStructure) {
    return { remaining: answer, errors, handled };
  }

  const remaining = { ...answer };
  for (const cfg of CLINICAL_SECTIONS) {
    const name = clinicalSubsectionName(cfg);
    // Only peel off a table this question actually published — another
    // section's answer is not ours to route.
    if (!(name in remaining) || !(name in hostStructure)) continue;

    const qi = questionInteractions[cfg.key];
    if (!qi) continue;

    const sectionAnswer = remaining[name];
    delete remaining[name];
    handled = true;

    const sectionReplyType = typeof replyType === 'string'
      ? replyType
      : replyType.subsections?.[name] || replyType.defaultReplyType;
    if (sectionReplyType === 'no_reply' || sectionReplyType === 'skip') continue;
    const result = await answerClinicalListQuestion(qi, sectionAnswer, sectionReplyType);
    if (Array.isArray(result)) errors.push(...result);
    else if (result) errors.push(result);
  }
  return { remaining, errors, handled };
}

// Normalises a Goal List answer to an array: bare array, { answer: [...] }
// envelope, or a single goal object.
function normalizeGoalAnswer(answer: any): any[] | null {
  if (Array.isArray(answer)) return answer;
  if (answer && Array.isArray(answer.answer)) return answer.answer;
  if (
    answer &&
    typeof answer === 'object' &&
    GOAL_SUBFIELDS.some((k) => k in answer)
  ) {
    return [answer];
  }
  return null;
}

// Fills a Goal List: navigate + expand, ensure enough cards, re-extract, then
// fill each goal by index (like WebPTInject's Assessment goals).
async function answerGoalListQuestion(
  questionObject: any,
  answer: any,
  replyType: string
) {
  return await questionMutex.runExclusive(async () => {
    const errors: any[] = [];
    const kind: string = questionObject.GoalKind;
    const tabLabel: string = questionObject.Tab;

    const parsed = normalizeGoalAnswer(answer);
    if (!parsed) {
      console.error('Goal List answer was not array-shaped:', answer);
      return {
        Question: questionObject.Key,
        Error: 'Goal List answer was not array-shaped'
      };
    }
    // Skip empty/None goals so we don't add blank cards.
    const goals = parsed.filter(
      (g) =>
        g &&
        typeof g === 'object' &&
        Object.values(g).some(
          (v) => !isEmptyOrNull(typeof v === 'string' ? v.trim() : v)
        )
    );

    if (tabLabel) {
      await navigateToTab(tabLabel);
      await clickExpandAll();
      await sleep(300);
    }

    // Add cards to match, then re-extract so new cards get live Input fns.
    await ensureGoalCards(kind, goals.length);
    extractGoalList(kind, tabLabel);
    const structure: any[] =
      questionInteractions[goalListKey(kind)]?.Structure || [];

    for (let i = 0; i < goals.length; i++) {
      const field = structure[i];
      if (!field) {
        errors.push({ Field: `Goal ${i + 1}`, Error: 'Field not found in Structure' });
        continue;
      }

      const fieldDetails = goals[i];
      if (!fieldDetails || typeof fieldDetails !== 'object') continue;

      for (const [subFieldKey, value] of Object.entries(
        fieldDetails as Record<string, any>
      )) {
        const resolvedSub =
          subFieldKey in field ? subFieldKey : fuzzyFindKey(field, subFieldKey);
        const sub = resolvedSub ? field[resolvedSub] : null;

        if (sub?.Input) {
          try {
            await sub.Input(value, replyType);
          } catch (error) {
            errors.push({
              Field: `Goal ${i + 1}.${subFieldKey}`,
              Error: `Error applying value: ${
                error instanceof Error ? error.message : String(error)
              }`
            });
          }
        } else {
          console.warn(`Input function not found for "Goal ${i + 1}.${subFieldKey}"`);
          errors.push({
            Field: `Goal ${i + 1}.${subFieldKey}`,
            Error: 'Input function not found'
          });
        }
      }
    }

    if (errors.length > 0) {
      console.error('Goal List errors:', errors);
    }
    const result = errors.length > 0 ? errors : null;
    applyAnswerToQuestionCache(questionObject.Key, answer, replyType, result);
    return result;
  });
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
